import {
  Stack,
  Title,
  Text,
  Image,
  Badge,
  Group,
  Box,
  Paper,
  Flex,
  Grid,
  ActionIcon,
  TextInput,
  Textarea,
  NumberInput,
  NumberInputHandlers,
  Select,
  MultiSelect,
  Chip,
  Button,
  Alert,
  Modal,
  Autocomplete,
  Menu,
  Collapse,
  Divider,
  Skeleton,
  Tooltip,
  Table,
  ScrollArea,
  SegmentedControl,
  Anchor,
  useMantineTheme,
  rem,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  IconClock,
  IconUsers,
  IconChefHat,
  IconBook2,
  IconChevronLeft,
  IconChevronUp,
  IconChevronDown,
  IconSlice,
  IconExternalLink,
  IconEdit,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconFlame,
  IconPlus,
  IconAlertCircle,
  IconToolsKitchen,
  IconPhoto,
  IconRosetteDiscountCheck,
  IconUser,
  IconReplace,
  IconDotsVertical,
  IconChartBar,
  IconReportMoney,
  IconCoin,
  IconRefresh,
  IconSalt,
  IconScale,
  IconShare,
  IconList,
  IconPrinter,
  IconBan,
  IconThumbUp,
  IconThumbDown,
  IconThumbUpFilled,
  IconThumbDownFilled,
  IconBookmark,
  IconBookmarkOff,
  IconStar,
  IconTrash,
  IconBrandPinterest,
} from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { ResponsiveDrawer } from '../UI/ResponsiveDrawer';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addFoodsToAvoid, isAlreadyAvoided } from '../../utils/foodsToAvoid';
import { afterPaint } from '../../utils/afterPaint';
import { shareLink } from '../../utils/shareLink';
import { Recipe, RecipeInstruction, Ingredient, CookHistory, RecipeCostBreakdown, CostBreakdownLine } from '../../types/mealPlan';
import { PantryItem } from '../../types/pantry';
import { MobileOptimized } from '../Layout/MobileOptimized';
import { formatCount } from '../../utils/formatCount';
import { NotesSkeleton } from './NotesSkeleton';
import { IngredientRowsSkeleton } from './IngredientRowsSkeleton';
import { StepTimerText } from './StepTimerText';
import { makeTimerOrigin, pendingStepFocus, useStepFocus } from '../../contexts/CookingTimersContext';
import { formatTime } from '../../utils/formatTime';
import { formatIngredientName, formatIngredientQuantity, formatIngredientPreparation } from '../../utils/formatIngredient';
import { getPrioritizedRestrictions, filterTagsForViewer } from '../../utils/recipeBadges';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import { householdSegment, HouseholdSegment } from '../../utils/householdSegment';
import { useAuth } from '../../hooks/useAuth';
import { useAppUser } from '../../hooks/useAppUser';
import { SubstitutionDrawer } from './SubstitutionDrawer';
import { NutritionDrawer } from './NutritionDrawer';
import SortableInstructionEditor from './SortableInstructionEditor';
import { SortableIngredient } from './SortableIngredient';
import classes from './RecipeDetailsView.module.css';
import { createInstacartRecipePage, createInstacartRecipePageForAssignment } from '../../services/instacartService';
import { getPantryItems } from '../../services/pantryService';
import { isItemInPantry, normalizeWithAliases, createDisplayName, suggestPantryCategory } from '../../utils/itemNormalization';
import { searchIngredients, getIngredientByName } from '../../data/ingredientDatabase';
import { RecipeImage } from '../UI/RecipeImage';
import { CookbookRibbon, TopPickRibbon } from '../UI/TopPickRibbon';
import { CostPerServingPopover } from '../UI/CostPerServingPopover';
import { MoreRestrictionsMenu } from '../UI/MoreRestrictionsMenu';
import { notifications } from '@mantine/notifications';
import { PantryItemDrawer } from '../Pantry/PantryItemDrawer';
import { RecipeRatingModal } from '../MealPlanning/RecipeRatingModal';
import { PantryCategory } from '../../types/pantry';
import { modals } from '@mantine/modals';
import { diffWords } from 'diff';
import { FractionQuantityInput, roundToValidCookingFraction } from '../UI/FractionQuantityInput';
import { printRecipe } from '../../utils/printRecipe';
import { AuthButton } from '../AuthButton';
import { buildRecipePath } from '../../utils/routeHelpers';
import { RecipeSourceAttribution, recipeSourceShortLabel, recipeSourceTooltip, sourceBadgeStyle, sourceTooltipProps } from './RecipeProvenanceBadges';
import { CuisineBadge } from '../UI/CuisineBadge';
import { deriveSlots } from '../Admin/CategorizationMenu';
import { UserAvatar } from '../UI/UserAvatar';
import { ownerDisplayName } from '../../utils/ownerName';

const fmtUsd = (n: number): string => `$${n.toFixed(2)}`;

const COST_STATUS_LABELS: Record<CostBreakdownLine['status'], string> = {
  priced: 'Priced',
  missing_record: 'No ingredient record',
  unconvertible: 'Unit not convertible',
  no_price: 'No price for any tier',
  not_an_ingredient: 'Not an ingredient',
  skipped: 'Free (water/staple)',
  optional: 'Optional (excluded)',
};

/**
 * Admin-only cost-breakdown modal body. Sorts the most expensive priced
 * ingredients to the top (the usual answer to "why is this so high?"), then
 * the holdouts that failed to price. Flags tier-blending — when a tier's price
 * was borrowed rather than resolved — and surfaces persisted-vs-live
 * divergence, since a stale persisted snapshot is a common cause of a price
 * that looks wrong relative to the live ingredient data.
 */
const CostBreakdownContent = ({ breakdown }: { breakdown: RecipeCostBreakdown }) => {
  const priced = breakdown.lines
    .filter((l) => l.status === 'priced')
    .sort((a, b) => (b.cost?.mid ?? 0) - (a.cost?.mid ?? 0));
  const other = breakdown.lines.filter((l) => l.status !== 'priced');
  const fullCoverage = breakdown.coverage >= 1;

  // Costs are stored as per-recipe totals. Default to per-serving so the rows
  // visibly add up to the headline per-serving number (divide by servings);
  // toggle to per-recipe to see the whole-recipe totals.
  const [view, setView] = useState<'serving' | 'recipe'>('serving');
  const div = view === 'serving' ? (breakdown.servings || 1) : 1;
  const pricedTotals = priced.reduce(
    (acc, l) => ({
      value: acc.value + (l.cost?.value ?? 0),
      mid: acc.mid + (l.cost?.mid ?? 0),
      premium: acc.premium + (l.cost?.premium ?? 0),
    }),
    { value: 0, mid: 0, premium: 0 }
  );

  const liveMid = breakdown.perServing.mid;
  const persistedMid = breakdown.persisted ? breakdown.persisted.perServing.mid : null;
  const diverges = persistedMid !== null && Math.abs(persistedMid - liveMid) >= 0.01;
  const savedOn = breakdown.persisted?.estimatedAt
    ? new Date(breakdown.persisted.estimatedAt).toLocaleDateString()
    : null;

  const noPriceTip = (line: CostBreakdownLine): string => {
    if (line.productTierCount === undefined) return 'No usable price.';
    return line.productTierCount > 0
      ? `Record has ${line.productTierCount} product tier(s) but none had a parseable price — usually a price-refresh re-pick whose package size no longer parses.`
      : 'Record has no product tiers — it was never populated with products.';
  };

  const renderRow = (line: CostBreakdownLine, i: number) => {
    const isPriced = line.status === 'priced';
    const borrowedTiers = isPriced
      ? (['value', 'mid', 'premium'] as const).filter(
          (t) => !(line.resolvedTiers ?? []).includes(t)
        )
      : [];
    return (
      <Table.Tr key={`${line.normalizedName}-${i}`} style={isPriced ? undefined : { opacity: 0.7 }}>
        <Table.Td>
          <Anchor
            href={`/admin/ingredients/${encodeURIComponent(line.normalizedName)}`}
            target="_blank"
            rel="noreferrer"
            size="sm"
            fw={500}
            title="Open ingredient DB record"
          >
            {line.name}
          </Anchor>
          <Text size="xs" c="dimmed">
            {line.quantity} {line.unit}
            {line.convertedQuantity !== undefined && line.defaultUnit
              ? ` → ${line.convertedQuantity} ${line.defaultUnit}`
              : ''}
          </Text>
        </Table.Td>
        {isPriced ? (
          <>
            <Table.Td style={{ textAlign: 'right' }}><Text size="sm" c="dimmed">{fmtUsd(line.cost!.value / div)}</Text></Table.Td>
            <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={600}>{fmtUsd(line.cost!.mid / div)}</Text></Table.Td>
            <Table.Td style={{ textAlign: 'right' }}><Text size="sm" c="dimmed">{fmtUsd(line.cost!.premium / div)}</Text></Table.Td>
            <Table.Td>
              {borrowedTiers.length > 0 ? (
                <Tooltip label={`No own price for: ${borrowedTiers.join(', ')} — borrowed from another tier`} multiline w={220}>
                  <Badge size="xs" color="yellow" variant="light">blended</Badge>
                </Tooltip>
              ) : null}
            </Table.Td>
          </>
        ) : (
          <Table.Td colSpan={4}>
            {line.status === 'no_price' ? (
              <Tooltip label={noPriceTip(line)} multiline w={260}>
                <Badge size="xs" color="red" variant="light">{COST_STATUS_LABELS[line.status]}</Badge>
              </Tooltip>
            ) : (
              <Badge size="xs" color={line.status === 'optional' || line.status === 'not_an_ingredient' || line.status === 'skipped' ? 'gray' : 'red'} variant="light">
                {COST_STATUS_LABELS[line.status]}
              </Badge>
            )}
          </Table.Td>
        )}
      </Table.Tr>
    );
  };

  return (
    <Stack gap="md">
      <Group gap="lg" wrap="wrap" align="flex-start">
        {persistedMid !== null && (
          <Box>
            <Text size="xs" c="dimmed">Shown to user (mid)</Text>
            <Text size="xl" fw={700} c={diverges ? 'orange' : undefined}>{fmtUsd(persistedMid)}</Text>
            <Text size="xs" c="dimmed">persisted{savedOn ? ` • saved ${savedOn}` : ''}</Text>
          </Box>
        )}
        <Box>
          <Text size="xs" c="dimmed">Live recompute (mid)</Text>
          <Text size="xl" fw={700}>{fmtUsd(liveMid)}</Text>
          <Text size="xs" c="dimmed">{fmtUsd(breakdown.perServing.value)}–{fmtUsd(breakdown.perServing.premium)} • {breakdown.servings} servings</Text>
        </Box>
        <Box>
          <Text size="xs" c="dimmed">Recipe total (mid)</Text>
          <Text size="xl" fw={700}>{fmtUsd(breakdown.total.mid)}</Text>
          <Text size="xs" c="dimmed">{fmtUsd(breakdown.total.value)}–{fmtUsd(breakdown.total.premium)}</Text>
        </Box>
        <Box>
          <Text size="xs" c="dimmed">Coverage (live)</Text>
          <Badge color={fullCoverage ? 'teal' : 'red'} variant="light" size="lg">
            {Math.round(breakdown.coverage * 100)}% ({breakdown.matchedCount}/{breakdown.nonOptionalCount})
          </Badge>
        </Box>
      </Group>

      {!fullCoverage && persistedMid !== null && (
        <Alert color="orange" variant="light" p="xs">
          <Text size="xs">
            Stale snapshot: users see <strong>{fmtUsd(persistedMid)}</strong>/serving{savedOn ? ` (saved ${savedOn})` : ''}, but a
            live recompute now prices only {breakdown.matchedCount}/{breakdown.nonOptionalCount} ingredients
            (<strong>{fmtUsd(liveMid)}</strong>, partial). One or more ingredients stopped pricing since the estimate
            was saved — the persisted price is a snapshot and won't refresh until those ingredients are healed
            (the 100%-coverage gate blocks re-persisting). See the red rows below.
          </Text>
        </Alert>
      )}

      {!fullCoverage && persistedMid === null && (
        <Alert color="red" variant="light" p="xs">
          <Text size="xs">
            No price is shown to users — live coverage is below 100% and no estimate was ever persisted.
            The totals above exclude the unpriced ingredients below.
          </Text>
        </Alert>
      )}

      {fullCoverage && diverges && (
        <Alert color="blue" variant="light" p="xs">
          <Text size="xs">
            Prices shifted since the persisted estimate: users see <strong>{fmtUsd(persistedMid!)}</strong>, live recompute is{' '}
            <strong>{fmtUsd(liveMid)}</strong>. The persisted value updates on the next price refresh.
          </Text>
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <Text size="sm" fw={600}>By ingredient</Text>
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => setView(v as 'serving' | 'recipe')}
          data={[
            { label: 'Per serving', value: 'serving' },
            { label: 'Per recipe', value: 'recipe' },
          ]}
        />
      </Group>

      <ScrollArea.Autosize mah={460}>
        <Table stickyHeader highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ingredient</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Value</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Mid</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Premium</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {priced.map(renderRow)}
            {other.map(renderRow)}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr style={{ fontWeight: 700, borderTop: '2px solid var(--mantine-color-gray-3)' }}>
              <Table.Td>Total {view === 'serving' ? '(per serving)' : '(per recipe)'}</Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>{fmtUsd(pricedTotals.value / div)}</Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>{fmtUsd(pricedTotals.mid / div)}</Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>{fmtUsd(pricedTotals.premium / div)}</Table.Td>
              <Table.Td></Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </ScrollArea.Autosize>

      <Text size="xs" c="dimmed">
        Live read-only recompute from current Kroger prices at location {breakdown.locationId}. "Blended" means that
        ingredient had no own price for a tier and borrowed another tier's — a common cause of an inflated value column.
      </Text>
    </Stack>
  );
};

interface RecipeDetailsViewProps {
  recipe: Recipe;
  onBack: () => void;
  onViewCanonical?: () => void;
  onSaveRecipe?: (updatedRecipe: Partial<Recipe>) => Promise<void>;
  canEdit?: boolean;
  startInEditMode?: boolean;
  /** Called when the editor closes (saved or cancelled). */
  onExitEditMode?: () => void;
  /**
   * The recipe is a list-item summary (no ingredients/instructions yet) shown
   * while the full recipe loads: those sections render skeleton rows. The
   * caller remounts the view once the full recipe arrives, so mount-time
   * state never seeds from the summary.
   */
  contentPending?: boolean;
  isOwner?: boolean; // Whether the current user is the owner of this recipe
  ownerId?: string; // The userId of the recipe owner (for navigation)
  isCatalogView?: boolean; // Whether viewing a catalog recipe directly (admin only)
  owner?: { uid: string; displayName?: string; photoURL?: string } | null; // Recipe owner info for attribution
  profilePublic?: boolean; // Whether the owner's profile is public
  ownerRecipeCount?: number;
  ownerTasteProfile?: { personalityName: string; personalityBlurb: string } | null;
  // Optional meal plan context for scaled recipes
  mealPlanId?: string;
  assignmentId?: string;
  cookHistory?: CookHistory | null;
}

const formatTimeAgo = (isoDate: string): string => {
  const now = new Date();
  const then = new Date(isoDate);
  // Compare by local calendar date to avoid timezone/hour-boundary issues
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thenLocal = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const days = Math.round((todayLocal.getTime() - thenLocal.getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (days < 365) return `${months} mo${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} yr${years === 1 ? '' : 's'} ago`;
};

const NotesEditor = React.memo(({ initialValue, onNotesChange }: { initialValue: string, onNotesChange: (notes: string) => void }) => {
  const [notes, setNotes] = useState(initialValue);

  useEffect(() => {
    onNotesChange(notes);
  }, [notes, onNotesChange]);

  useEffect(() => {
    setNotes(initialValue);
  }, [initialValue]);

  return (
    <Textarea
      label="Notes"
      variant="unstyled"
      value={notes}
      onChange={(e) => setNotes(e.currentTarget.value)}
      placeholder="Add any notes for this recipe..."
      minRows={3}
      autosize
    />
  );
});

const DescriptionEditor = React.memo(({ initialValue, onDescriptionChange }: { initialValue: string, onDescriptionChange: (description: string) => void }) => {
  const [description, setDescription] = useState(initialValue);

  useEffect(() => {
    onDescriptionChange(description);
  }, [description, onDescriptionChange]);

  useEffect(() => {
    setDescription(initialValue);
  }, [initialValue]);

  return (
    <Textarea
      variant="unstyled"
      value={description}
      onChange={(e) => {
        const value = e.currentTarget.value;
        if (value.length <= 500) {
          setDescription(value);
        }
      }}
      minRows={2}
      maxRows={4}
      maxLength={500}
      autosize
      withAsterisk={false}
      placeholder="Recipe description..."
    />
  );
});

const CuisineEditor = React.memo(({ initialValue, onCuisineChange }: { initialValue: string, onCuisineChange: (cuisine: string) => void }) => {
  const [cuisine, setCuisine] = useState(initialValue);

  useEffect(() => {
    onCuisineChange(cuisine);
  }, [cuisine, onCuisineChange]);

  useEffect(() => {
    setCuisine(initialValue);
  }, [initialValue]);

  return (
    <Autocomplete
      value={cuisine}
      onChange={(value) => {
        if (value.length <= 50) {
          setCuisine(value);
        }
      }}
      data={[
        'Italian',
        'Chinese', 
        'Japanese',
        'Indian',
        'Mexican',
        'French',
        'Thai',
        'American',
        'Korean',
        'Vietnamese',
        'Mediterranean',
        'Middle Eastern',
        'Latin',
        'African'
      ]}
      placeholder="Cuisine..."
      maxLength={50}
      size="sm"
      styles={{
        input: {
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(2px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          color: 'var(--mantine-color-dark-9)'
        }
      }}
    />
  );
});

const IngredientEditor = React.memo(({
  index,
  ingredient,
  onIngredientChange,
  onRemove,
  onIngredientNameChange,
  noBorder
}: {
  index: number;
  ingredient: any;
  onIngredientChange: (index: number, updatedIngredient: any) => void;
  onRemove: (index: number) => void;
  onIngredientNameChange?: (oldName: string) => void;
  noBorder?: boolean;
}) => {
  const [localIngredient, setLocalIngredient] = useState(ingredient);
  const [localQuantityInput, setLocalQuantityInput] = useState('');
  const [previousName, setPreviousName] = useState(ingredient.name);

  useEffect(() => {
    onIngredientChange(index, localIngredient);
  }, [localIngredient, index, onIngredientChange]);

  useEffect(() => {
    setLocalIngredient(ingredient);
  }, [ingredient]);

  const updateIngredient = (updates: any) => {
    const updatedIngredient = { ...localIngredient, ...updates };
    setLocalIngredient(updatedIngredient);
    // Also immediately update the parent ref
    onIngredientChange(index, updatedIngredient);
  };

  return (
    <Paper key={index} p="md" radius="lg" withBorder={!noBorder}>
      <Stack gap="sm">
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <Box style={{ pointerEvents: 'auto' }}>
            <FractionQuantityInput
              value={localIngredient.quantity}
              onChange={(value) => updateIngredient({ quantity: value })}
              localInput={localQuantityInput}
              onLocalInputChange={setLocalQuantityInput}
              variant="unstyled"
              size="sm"
              width={80}
            />
          </Box>
          <Box style={{ pointerEvents: 'auto' }}>
            <Select
              label="Unit"
              variant="unstyled"
              value={localIngredient.unit}
              onChange={(value) => updateIngredient({ unit: value || '' })}
              data={[
                {
                  group: 'Volume',
                  items: [
                    { value: 'teaspoon', label: 'tsp' },
                    { value: 'tablespoon', label: 'tbsp' },
                    { value: 'fluid ounce', label: 'fl oz' },
                    { value: 'cup', label: 'cup' },
                    { value: 'pint', label: 'pt' },
                    { value: 'quart', label: 'qt' },
                    { value: 'gallon', label: 'gal' },
                    { value: 'dash', label: 'dash' },
                    { value: 'pinch', label: 'pinch' },
                  ]
                },
                {
                  group: 'Weight',
                  items: [
                    { value: 'ounce', label: 'oz' },
                    { value: 'pound', label: 'lb' },
                  ]
                },
                {
                  group: 'Count',
                  items: [
                    { value: 'each', label: 'each' },
                    { value: 'whole', label: 'whole' },
                    { value: 'piece', label: 'piece' },
                    { value: 'slice', label: 'slice' },
                    { value: 'clove', label: 'clove' },
                    { value: 'can', label: 'can' },
                    { value: 'bottle', label: 'bottle' },
                    { value: 'jar', label: 'jar' },
                    { value: 'package', label: 'package' },
                    { value: 'bunch', label: 'bunch' },
                    { value: 'head', label: 'head' },
                    { value: 'stick', label: 'stick' },
                  ]
                }
              ]}
              style={{ width: 90 }}
              styles={{
                input: {
                    border: 'none',
                },
              }}
              size="sm"
              searchable
              error={localIngredient.unit.trim().length === 0 ? 'Required' : null}
            />
          </Box>
          <Box style={{ flex: 1, pointerEvents: 'auto' }}>
            <Group gap={0}>
              <Autocomplete
                label="Ingredient"
                variant="unstyled"
                value={localIngredient.name}
                onChange={(value) => {
                  if (value.length <= 100) {
                    updateIngredient({ name: value });
                  }
                }}
                onBlur={() => {
                  // Update available ingredients when user finishes typing ingredient name
                  if (onIngredientNameChange && previousName !== localIngredient.name) {
                    onIngredientNameChange(previousName);
                    setPreviousName(localIngredient.name);
                  }
                }}
                onOptionSubmit={(value) => {
                  // Auto-populate unit when selecting from autocomplete
                  const ingredientData = getIngredientByName(value);

                  // Use setTimeout to ensure state update happens after current render cycle
                  setTimeout(() => {
                    updateIngredient({
                      name: value,
                      unit: ingredientData?.commonUnit || localIngredient.unit
                    });
                    // Update available ingredients when selecting from autocomplete
                    if (onIngredientNameChange && previousName !== value) {
                      onIngredientNameChange(previousName);
                      setPreviousName(value);
                    }
                  }, 0);
                }}
                data={localIngredient.name.length >= 2 ? searchIngredients(localIngredient.name, 8).map(ing => ing.displayName ?? ing.name) : []}
                size="sm"
                maxLength={100}
                error={localIngredient.name.trim().length === 0 ? 'Required' : null}
                style={{ flex: 1 }}
                styles={{
                  input: {
                      border: 'none',
                  },
                }}
                limit={8}
                comboboxProps={{ withinPortal: true }}
                rightSection={
                  <ActionIcon
                    color="orange"
                    variant="subtle"
                    onClick={() => onRemove(index)}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                }
              />
            </Group>
          </Box>
        </Group>
        <Group gap="sm" align="flex-start">
          <Box style={{ flex: 1, pointerEvents: 'auto' }}>
            <TextInput
              label="Preparation"
              variant="unstyled"
              value={localIngredient.preparation || ''}
              onChange={(event) => {
                const value = event.currentTarget.value;
                if (value.length <= 100) {
                  updateIngredient({ preparation: value });
                }
              }}
              placeholder="diced, chopped, etc."
              size="sm"
              maxLength={100}
            />
          </Box>
          <Box style={{ alignSelf: 'flex-end', pointerEvents: 'auto' }}>
            <Button
              variant={localIngredient.optional ? "filled" : "outline"}
              onClick={() => updateIngredient({ optional: !localIngredient.optional })}
            >
              Optional
            </Button>
          </Box>
        </Group>
      </Stack>
    </Paper>
  );
});

export const RecipeDetailsView = ({ recipe, onBack, onViewCanonical, onSaveRecipe, canEdit, startInEditMode, onExitEditMode, contentPending = false, isOwner = true, ownerId, owner, profilePublic = false, isCatalogView = false, ownerRecipeCount, ownerTasteProfile, mealPlanId, assignmentId, cookHistory }: RecipeDetailsViewProps) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { user: appUser, profileLoaded } = useAppUser();
  const isAdmin = appUser?.isAdmin || !!appUser?._adminOverride;
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTabletOrSmaller = useMediaQuery('(max-width: 1280px)');
  const isScaledRecipe = recipe.originalServings && recipe.originalServings !== recipe.servings;
  const [isEditing, setIsEditing] = useState(startInEditMode || false);
  const [editedRecipe, setEditedRecipe] = useState<Recipe>(recipe);
  const additionalMealsEnabled = useFeatureFlag('additional_meals');
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [triggeringCook, setTriggeringCook] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [refreshingNutrition, setRefreshingNutrition] = useState(false);
  const [loadingCostBreakdown, setLoadingCostBreakdown] = useState(false);
  const [refreshingCost, setRefreshingCost] = useState(false);

  // Mirror server-recomputed cost/nutrition into editedRecipe (which the
  // metadata stats read). Saving an edit updates the `recipe` prop with the
  // backend's recomputed values; sync just those fields so the displayed cost
  // updates without a page reload and without clobbering in-progress edits.
  // Admin refresh actions set editedRecipe directly and leave the prop
  // unchanged, so they aren't reverted here.
  useEffect(() => {
    setEditedRecipe(prev => ({
      ...prev,
      costEstimate: recipe.costEstimate,
      nutrition: recipe.nutrition,
    }));
  }, [recipe.costEstimate, recipe.nutrition]);
  const [curateLoading, setCurateLoading] = useState(false);
  const [addingToInstacart, setAddingToInstacart] = useState(false);
  const [pantryItems, setPantryItems] = useState<PantryItem[]>([]);
  const [pantryLoading, setPantryLoading] = useState(true);
  
  const [imageModalOpened, setImageModalOpened] = useState(false);
  const [ownerBioExpanded, setOwnerBioExpanded] = useState(false);

  // Rating state
  const [localRating, setLocalRating] = useState<'up' | 'down' | undefined>(recipe.rating);
  const [isRating, setIsRating] = useState(false);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  // True while a menu-item navigation is closing the context menu: the close
  // must skip its exit transition and paint before the route changes, or
  // Chrome's predictive-back screenshot keeps the open menu (see afterPaint).
  const [instantMenuClose, setInstantMenuClose] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(recipe.isSaved ?? true);
  const [isSavingCookbook, setIsSavingCookbook] = useState(false);

  // Substitution state
  const [substitutionDrawerOpened, setSubstitutionDrawerOpened] = useState(false);
  const [substitutionTarget, setSubstitutionTarget] = useState<Ingredient | null>(null);
  const [substitutionLoading, setSubstitutionLoading] = useState(false);
  const [substitutionError, setSubstitutionError] = useState<string | null>(null);

  // Nutrition drawer state
  const [nutritionDrawerOpened, setNutritionDrawerOpened] = useState(false);
  const [showNutritionOverlay, setShowNutritionOverlay] = useState(false);

  // Mobile ingredient drawer state
  const [ingredientDrawerOpened, setIngredientDrawerOpened] = useState(false);

  // Pantry drawer state
  const [pantryDrawerOpened, setPantryDrawerOpened] = useState(false);
  const [selectedIngredientForPantry, setSelectedIngredientForPantry] = useState<Ingredient | null>(null);
  const [addingToPantryIngredient, setAddingToPantryIngredient] = useState<string | null>(null);

  // Track when ingredients section scrolls out of view for mobile FAB
  const ingredientsTitleRef = useRef<HTMLHeadingElement>(null);
  const [showIngredientsFab, setShowIngredientsFab] = useState(false);

  // Arriving from (or tapping, while here) a tray timer: scroll its step
  // into view and briefly flash it.
  const location = useLocation();
  const stepFocusCtx = useStepFocus();
  const focusStep = pendingStepFocus(stepFocusCtx, recipe.id);
  const [flashStepIndex, setFlashStepIndex] = useState<number | null>(null);
  useEffect(() => {
    if (focusStep == null) return;
    stepFocusCtx?.clearStepFocus();
    // After paint, so it lands after useScrollToTop's reset on navigation.
    afterPaint(() => {
      document.getElementById(`recipe-step-${focusStep}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    setFlashStepIndex(focusStep);
    // Re-run only on a new request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStep]);
  // Own effect: clearing the request above re-runs that one, whose cleanup
  // would otherwise cancel this timeout.
  useEffect(() => {
    if (flashStepIndex == null) return;
    const t = window.setTimeout(() => setFlashStepIndex(null), 1800);
    return () => window.clearTimeout(t);
  }, [flashStepIndex]);

  // Measure CTA banner height for FAB positioning
  const ctaBannerRef = useRef<HTMLDivElement>(null);
  const [ctaBannerHeight, setCtaBannerHeight] = useState(0);

  useEffect(() => {
    const el = ctaBannerRef.current;
    if (!el) {
      // Banner unmounted (e.g. signed in) — drop the stale offset.
      setCtaBannerHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => {
      setCtaBannerHeight(el.getBoundingClientRect().height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [user, authLoading]);

  // Bottom offset for floating controls: clears the CTA banner when shown.
  // Published as a CSS variable so the app-level cooking timer tray (outside
  // this tree) rides up alongside the ingredients FAB.
  const floatingControlsBottom = ctaBannerHeight > 0 ? ctaBannerHeight + 16 : 24;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--floating-controls-bottom', `${floatingControlsBottom}px`);
    return () => {
      root.style.removeProperty('--floating-controls-bottom');
    };
  }, [floatingControlsBottom]);

  useEffect(() => {
    const el = ingredientsTitleRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowIngredientsFab(false);
        } else {
          // Only show FAB when ingredients scrolled off the top (not below viewport)
          setShowIngredientsFab(entry.boundingClientRect.top < 0);
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Instruction-to-ingredient hover/click highlight state
  const [highlightedIngredients, setHighlightedIngredients] = useState<Set<string>>(new Set());
  const [lockedStepIndex, setLockedStepIndex] = useState<number | null>(null);
  const lockedStepRef = useRef<number | null>(null);

  // Ingredient-to-instruction highlight state (reverse direction)
  const [highlightedSteps, setHighlightedSteps] = useState<Set<number>>(new Set());
  const [activeIngredientHighlight, setActiveIngredientHighlight] = useState<string | null>(null);
  const [lockedIngredientName, setLockedIngredientName] = useState<string | null>(null);
  const lockedIngredientRef = useRef<string | null>(null);

  // Keep refs in sync for the document click listener
  useEffect(() => { lockedStepRef.current = lockedStepIndex; }, [lockedStepIndex]);
  useEffect(() => { lockedIngredientRef.current = lockedIngredientName; }, [lockedIngredientName]);

  // Click anywhere to unlock pinned step or ingredient
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Don't clear locks when clicking inside drawers/modals (rendered in portals)
      if ((e.target as HTMLElement).closest('.mantine-Drawer-root, .mantine-Modal-root, .mantine-Overlay-root')) return;
      if (lockedStepRef.current !== null) {
        setLockedStepIndex(null);
        setHighlightedIngredients(new Set());

      }
      if (lockedIngredientRef.current !== null) {
        setLockedIngredientName(null);
        setActiveIngredientHighlight(null);
        setHighlightedSteps(new Set());
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Foods to avoid: tracks the merged app user's profile (session-cached on
  // remounts, refreshed by useAppUser); handleAddToFoodsToAvoid then updates
  // it directly from the server response between refreshes.
  const [foodsToAvoid, setFoodsToAvoid] = useState<string[]>(
    appUser?.familyProfile?.dislikes ?? []
  );
  useEffect(() => {
    if (appUser?.familyProfile?.dislikes) {
      setFoodsToAvoid(appUser.familyProfile.dislikes);
    }
  }, [appUser?.familyProfile?.dislikes]);

  // User's dietary restrictions for prioritization, off the merged app user's
  // profile. null = not loaded yet (skeletons), array (even empty) = loaded
  const userDietaryRestrictions: string[] | null = authLoading
    ? null
    : !user
      ? []
      : appUser?.familyProfile
        ? appUser.familyProfile.dietaryRestrictions || []
        : profileLoaded
          ? []
          : null;

  // Household framing for the viewer; gates family-only tags like "kid-friendly"
  const userHouseholdSegment: HouseholdSegment | undefined = householdSegment(
    appUser?.familyProfile
  );


  // Ensure quantities are rounded to cooking fractions when component loads
  useEffect(() => {
    if (editedRecipe.ingredients) {
      const needsRounding = editedRecipe.ingredients.some(ingredient => {
        const rounded = roundToValidCookingFraction(ingredient.quantity);
        return Math.abs(ingredient.quantity - rounded) > 0.001;
      });
      
      if (needsRounding) {
        const roundedIngredients = editedRecipe.ingredients.map(ingredient => ({
          ...ingredient,
          quantity: roundToValidCookingFraction(ingredient.quantity)
        }));
        setEditedRecipe(prev => ({ ...prev, ingredients: roundedIngredients }));
      }
    }
  }, [recipe.id, editedRecipe.ingredients]); // Only run when recipe changes
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [signedImageUrls, setSignedImageUrls] = useState<string[]>([]);
  const [loadingSignedUrls, setLoadingSignedUrls] = useState(false);
  const ingredientRefs = useRef<HTMLInputElement[]>([]);
  const instructionRefs = useRef<HTMLTextAreaElement[]>([]);
  const [currentNotes, setCurrentNotes] = useState(recipe.notes || '');
  const [currentDescription, setCurrentDescription] = useState(recipe.description || '');
  const [currentCuisine, setCurrentCuisine] = useState(recipe.cuisine || '');
  const instructionsRef = useRef<RecipeInstruction[]>(editedRecipe.instructions as RecipeInstruction[] || []);
  const ingredientsRef = useRef<any[]>(editedRecipe.ingredients || []);

  // Create stable IDs for ingredients to avoid re-render issues during drag
  const ingredientIdsRef = useRef<string[]>([]);
  const getIngredientId = useCallback((index: number) => {
    // Ensure we have enough IDs
    while (ingredientIdsRef.current.length <= index) {
      ingredientIdsRef.current.push(`ingredient-${Date.now()}-${ingredientIdsRef.current.length}`);
    }
    return ingredientIdsRef.current[index];
  }, []);

  // Create stable IDs for instructions to avoid re-render issues during drag
  const instructionIdsRef = useRef<string[]>([]);
  const getInstructionId = useCallback((index: number) => {
    // Ensure we have enough IDs
    while (instructionIdsRef.current.length <= index) {
      instructionIdsRef.current.push(`instruction-${Date.now()}-${instructionIdsRef.current.length}`);
    }
    return instructionIdsRef.current[index];
  }, []);

  const [availableIngredientNames, setAvailableIngredientNames] = useState<string[]>(
    editedRecipe.ingredients?.map(ing => ing.name).filter(name => name.trim().length > 0) || []
  );
  const [activeInstructionId, setActiveInstructionId] = useState<string | null>(null);
  const [activeIngredientId, setActiveIngredientId] = useState<string | null>(null);

  // Inline ingredient editing state (for view mode quick edits)
  const [editingIngredientIndex, setEditingIngredientIndex] = useState<number | null>(null);
  const tempEditedIngredientRef = useRef<any>(null);
  const [savingInlineEdit, setSavingInlineEdit] = useState(false);

  // Cook event tracking for preference learning (visibility-aware heuristic)
  // If the user has this recipe visible for 5+ minutes, they're likely cooking from it.
  const COOK_EVENT_MIN_VISIBLE_SECONDS = 900; // 15 minutes
  const viewStartTimeRef = useRef<number | null>(null);
  const visibleDurationRef = useRef<number>(0);
  const cookEventFiredRef = useRef<boolean>(false);
  const trackingRecipeIdRef = useRef<string>(recipe.id);

  const fireCookEventIfThresholdMet = useCallback(async () => {
    if (cookEventFiredRef.current || !user || !isOwner || isAdmin || isEditing) return;
    if (!viewStartTimeRef.current) return;

    const currentVisibleTime =
      visibleDurationRef.current +
      (Date.now() - viewStartTimeRef.current) / 1000;

    if (currentVisibleTime >= COOK_EVENT_MIN_VISIBLE_SECONDS) {
      cookEventFiredRef.current = true;

      try {
        const token = await user.getIdToken();
        await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/cook-event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            recipeId: recipe.id,
            mealPlanId,
            stepsCompleted: recipe.instructions?.length || 0,
            totalSteps: recipe.instructions?.length || 0,
          }),
        });
        console.log('[RecipeDetailsView] Cook event logged (visibility heuristic)');
      } catch (error) {
        console.error('[RecipeDetailsView] Failed to log cook event:', error);
        cookEventFiredRef.current = false;
      }
    }
  }, [user, isOwner, isAdmin, isEditing, recipe.id, recipe.instructions?.length, mealPlanId]);

  // Reset tracking when recipe changes
  useEffect(() => {
    if (recipe.id !== trackingRecipeIdRef.current) {
      trackingRecipeIdRef.current = recipe.id;
      viewStartTimeRef.current = document.hidden ? null : Date.now();
      visibleDurationRef.current = 0;
      cookEventFiredRef.current = false;
    }
  }, [recipe.id]);

  // Track visible time using Page Visibility API
  useEffect(() => {
    viewStartTimeRef.current = document.hidden ? null : Date.now();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden — accumulate elapsed visible time
        if (viewStartTimeRef.current) {
          visibleDurationRef.current +=
            (Date.now() - viewStartTimeRef.current) / 1000;
          viewStartTimeRef.current = null;
        }
      } else {
        // Page visible — start tracking again
        viewStartTimeRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [recipe.id]);

  // Periodic check for cook event threshold (every 30 seconds)
  useEffect(() => {
    const intervalId = setInterval(() => {
      fireCookEventIfThresholdMet();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fireCookEventIfThresholdMet]);

  // Drag-and-drop sensors for instruction and ingredient reordering
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Callback functions for instruction editing
  const handleInstructionTextChange = React.useCallback((index: number, text: string) => {
    instructionsRef.current[index] = { ...instructionsRef.current[index], text };
  }, []);

  const handleInstructionIngredientsChange = React.useCallback((index: number, ingredients: string[]) => {
    instructionsRef.current[index] = { ...instructionsRef.current[index], ingredients };
  }, []);

  const handleInstructionRemove = React.useCallback((index: number) => {
    const newInstructions = instructionsRef.current.filter((_, i) => i !== index);
    instructionsRef.current = newInstructions;
    setEditedRecipe(prev => ({ ...prev, instructions: newInstructions }));
  }, []);

  const handleInstructionReorder = React.useCallback((oldIndex: number, newIndex: number) => {
    const reordered = arrayMove(instructionsRef.current, oldIndex, newIndex);
    instructionsRef.current = reordered;
    // Also reorder the stable IDs array to keep them in sync
    instructionIdsRef.current = arrayMove(instructionIdsRef.current, oldIndex, newIndex);
    setEditedRecipe(prev => ({ ...prev, instructions: reordered }));
  }, []);

  const handleInstructionDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveInstructionId(event.active.id.toString());
  }, []);

  const handleInstructionDragEnd = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Find the indices by looking up the stable IDs
      let oldIndex = -1;
      let newIndex = -1;

      for (let i = 0; i < instructionsRef.current.length; i++) {
        const id = getInstructionId(i);
        if (id === active.id) oldIndex = i;
        if (id === over.id) newIndex = i;
      }

      if (oldIndex !== -1 && newIndex !== -1) {
        handleInstructionReorder(oldIndex, newIndex);
      }
    }

    setActiveInstructionId(null);
  }, [handleInstructionReorder, getInstructionId]);

  // Callback functions for ingredient editing
  const handleIngredientChange = React.useCallback((index: number, updatedIngredient: any) => {
    ingredientsRef.current[index] = updatedIngredient;
  }, []);

  const updateAvailableIngredientNames = React.useCallback(() => {
    const ingredientNames = ingredientsRef.current.map(ing => ing.name).filter(name => name.trim().length > 0);
    setAvailableIngredientNames(ingredientNames);
  }, []);

  const handleIngredientReorder = React.useCallback((oldIndex: number, newIndex: number) => {
    const reordered = arrayMove(ingredientsRef.current, oldIndex, newIndex);
    ingredientsRef.current = reordered;
    // Also reorder the stable IDs array to keep them in sync
    ingredientIdsRef.current = arrayMove(ingredientIdsRef.current, oldIndex, newIndex);
    setEditedRecipe(prev => ({ ...prev, ingredients: reordered }));
    updateAvailableIngredientNames();
  }, [updateAvailableIngredientNames]);

  const handleIngredientDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveIngredientId(event.active.id.toString());
  }, []);

  const handleIngredientDragEnd = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Find the indices by looking up the stable IDs
      let oldIndex = -1;
      let newIndex = -1;

      for (let i = 0; i < ingredientsRef.current.length; i++) {
        const id = getIngredientId(i);
        if (id === active.id) oldIndex = i;
        if (id === over.id) newIndex = i;
      }

      if (oldIndex !== -1 && newIndex !== -1) {
        handleIngredientReorder(oldIndex, newIndex);
      }
    }

    setActiveIngredientId(null);
  }, [handleIngredientReorder, getIngredientId]);

  const handleIngredientNameChange = React.useCallback((oldName: string) => {
    // Remove old ingredient name from all instructions
    const updatedInstructions = instructionsRef.current.map(instruction => ({
      ...instruction,
      ingredients: instruction.ingredients?.filter(ingredientName => 
        ingredientName !== oldName
      ) || []
    }));
    instructionsRef.current = updatedInstructions;
    
    // Update available ingredient names
    updateAvailableIngredientNames();
    
    // Update the recipe state to trigger instruction editor re-renders
    setEditedRecipe(prev => ({ 
      ...prev, 
      instructions: updatedInstructions
    }));
  }, [updateAvailableIngredientNames]);

  const handleIngredientRemove = React.useCallback((index: number) => {
    const removedIngredientName = ingredientsRef.current[index]?.name;
    const newIngredients = ingredientsRef.current.filter((_, i) => i !== index);
    ingredientsRef.current = newIngredients;
    
    // Remove this ingredient from any instruction references
    const updatedInstructions = instructionsRef.current.map(instruction => ({
      ...instruction,
      ingredients: instruction.ingredients?.filter(ingredientName => 
        ingredientName !== removedIngredientName
      ) || []
    }));
    instructionsRef.current = updatedInstructions;
    
    // Update available ingredient names for instruction editors
    updateAvailableIngredientNames();
    
    setEditedRecipe(prev => ({ 
      ...prev, 
      ingredients: newIngredients,
      instructions: updatedInstructions
    }));
  }, [updateAvailableIngredientNames]);

  // Inline ingredient editing handlers (for view mode quick edits)
  const handleStartInlineEdit = useCallback((index: number, ingredient: any) => {
    setEditingIngredientIndex(index);
    tempEditedIngredientRef.current = { ...ingredient };
  }, []);

  const handleSaveInlineEdit = useCallback(async () => {
    if (editingIngredientIndex === null || !tempEditedIngredientRef.current) return;

    const oldIngredient = recipe.ingredients[editingIngredientIndex];
    const updatedIngredients = [...recipe.ingredients];
    updatedIngredients[editingIngredientIndex] = tempEditedIngredientRef.current;

    // If ingredient name changed, remove old name from instruction references (matching edit mode behavior)
    let updatedInstructions = recipe.instructions;
    if (oldIngredient.name !== tempEditedIngredientRef.current.name) {
      updatedInstructions = (recipe.instructions as RecipeInstruction[] || []).map(instruction => ({
        ...instruction,
        ingredients: instruction.ingredients?.filter(ingredientName =>
          ingredientName !== oldIngredient.name
        ) || []
      }));
    }

    setSavingInlineEdit(true);
    try {
      const updateData: any = { ingredients: updatedIngredients };
      if (oldIngredient.name !== tempEditedIngredientRef.current.name) {
        updateData.instructions = updatedInstructions;
      }

      if (onSaveRecipe) {
        // Use the callback if provided
        await onSaveRecipe(updateData);
      } else {
        // Otherwise make the API call directly
        const token = await user?.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipe.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update ingredient');
        }
      }

      setEditingIngredientIndex(null);
      tempEditedIngredientRef.current = null;
    } catch (error) {
      console.error('Error updating ingredient:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update ingredient',
        color: 'red',
      });
    } finally {
      setSavingInlineEdit(false);
    }
  }, [editingIngredientIndex, recipe, user, onSaveRecipe]);

  const handleCancelInlineEdit = useCallback(() => {
    setEditingIngredientIndex(null);
    tempEditedIngredientRef.current = null;
  }, []);

  const handleInlineIngredientChange = useCallback((index: number, updatedIngredient: any) => {
    tempEditedIngredientRef.current = updatedIngredient;
  }, []);

  const handleRemoveInlineIngredient = useCallback(async () => {
    if (editingIngredientIndex === null) return;

    const removedIngredientName = recipe.ingredients[editingIngredientIndex]?.name;
    const updatedIngredients = recipe.ingredients.filter((_, i) => i !== editingIngredientIndex);

    // Remove this ingredient from any instruction references
    const updatedInstructions = (recipe.instructions as RecipeInstruction[] || []).map(instruction => ({
      ...instruction,
      ingredients: instruction.ingredients?.filter(ingredientName =>
        ingredientName !== removedIngredientName
      ) || []
    }));

    setSavingInlineEdit(true);
    try {
      if (onSaveRecipe) {
        await onSaveRecipe({
          ingredients: updatedIngredients,
          instructions: updatedInstructions
        });
      } else {
        const token = await user?.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipe.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            ingredients: updatedIngredients,
            instructions: updatedInstructions
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to remove ingredient');
        }
      }

      setEditingIngredientIndex(null);
      tempEditedIngredientRef.current = null;
    } catch (error) {
      console.error('Error removing ingredient:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to remove ingredient',
        color: 'red',
      });
    } finally {
      setSavingInlineEdit(false);
    }
  }, [editingIngredientIndex, recipe, user, onSaveRecipe]);

  const handleGetSubstitutions = async (ingredient: Ingredient) => {
    setSubstitutionTarget(ingredient);
    setSubstitutionDrawerOpened(true);
    setSubstitutionLoading(true);
    setSubstitutionError(null);

    try {
      const token = await user?.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipe.id}/substitutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetIngredient: ingredient,
          ...(ownerId && { ownerId }),
          ...(mealPlanId && assignmentId && {
            mealPlanId,
            assignmentId
          })
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get substitutions');
      }

      const data = await response.json();

      // Update the ingredient with substitutions in the target
      setSubstitutionTarget(prev => prev ? { ...prev, substitutions: data.substitutions } : null);

      // Update the ingredient with substitutions in the local state
      if (isEditing) {
        const updatedIngredients = editedRecipe.ingredients.map(ing =>
          ing.name === ingredient.name &&
          ing.quantity === ingredient.quantity &&
          ing.unit === ingredient.unit
            ? { ...ing, substitutions: data.substitutions }
            : ing
        );
        setEditedRecipe(prev => ({ ...prev, ingredients: updatedIngredients }));
        ingredientsRef.current = updatedIngredients;
      }

    } catch (error) {
      console.error('Error getting substitutions:', error);
      setSubstitutionError(error instanceof Error ? error.message : 'Failed to get substitutions');
    } finally {
      setSubstitutionLoading(false);
    }
  };

  const handleToggleCookbook = async () => {
    if (!user) return;
    setContextMenuOpen(false);
    const wasSaved = localIsSaved;
    setLocalIsSaved(!wasSaved);
    setIsSavingCookbook(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/cookbook/${recipe.id}`,
        {
          method: wasSaved ? 'DELETE' : 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      if (!response.ok) {
        setLocalIsSaved(wasSaved);
      }
    } catch (error) {
      console.error('Error toggling cookbook:', error);
      setLocalIsSaved(wasSaved);
    } finally {
      setIsSavingCookbook(false);
    }
  };

  const handleDeleteRecipe = async () => {
    if (!user) return;
    setContextMenuOpen(false);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipe.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete recipe');

      onBack();

      const notificationId = `delete-${recipe.id}`;
      const UndoButton = () => {
        const [isUndoing, setIsUndoing] = useState(false);
        return (
          <Button
            size="xs"
            variant="subtle"
            color="orange"
            loading={isUndoing}
            onClick={async () => {
              setIsUndoing(true);
              try {
                const undoToken = await user.getIdToken();
                const undoResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipe.id}/undo`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${undoToken}` },
                });
                if (undoResponse.ok) {
                  notifications.hide(notificationId);
                  navigate(buildRecipePath(recipe, ownerId || user.uid));
                }
              } finally {
                setIsUndoing(false);
              }
            }}
          >
            Undo
          </Button>
        );
      };

      notifications.show({
        id: notificationId,
        title: 'Recipe deleted',
        message: (
          <Group>
            <Text size="sm">"{recipe.name}" has been deleted</Text>
            <UndoButton />
          </Group>
        ),
        color: 'orange',
        autoClose: 5000,
        withCloseButton: true,
      });
    } catch (error) {
      console.error('Error deleting recipe:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete recipe',
        color: 'red',
        autoClose: 5000,
      });
    }
  };

  const handleRateUp = async () => {
    if (!user || localRating) return;
    setContextMenuOpen(false);
    setLocalRating('up');
    setIsRating(true);
    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/rate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recipeId: recipe.id, rating: 'up' }),
      });
    } catch (error) {
      console.error('Error rating recipe:', error);
      setLocalRating(undefined);
    } finally {
      setIsRating(false);
    }
  };

  const handleRateDown = () => {
    if (localRating) return;
    setContextMenuOpen(false);
    setTimeout(() => setRatingModalOpen(true), 0);
  };

  const handleRateDownSubmit = async (selectedTags: string[], dietaryViolations: string[]) => {
    if (!user) return;
    setRatingModalOpen(false);
    setLocalRating('down');
    setIsRating(true);
    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/rate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipeId: recipe.id,
          rating: 'down',
          feedbackTags: selectedTags,
          dietaryViolations: dietaryViolations.length > 0 ? dietaryViolations : undefined,
        }),
      });
    } catch (error) {
      console.error('Error rating recipe:', error);
      setLocalRating(undefined);
    } finally {
      setIsRating(false);
    }
  };

  const handleUndoRating = async () => {
    if (!user || !localRating) return;
    const previousRating = localRating;
    setLocalRating(undefined);
    setIsRating(true);
    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/rate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recipeId: recipe.id, rating: 'none' }),
      });
    } catch (error) {
      console.error('Error undoing rating:', error);
      setLocalRating(previousRating);
    } finally {
      setIsRating(false);
    }
  };

  const handleAddAsStaple = async (ingredient: Ingredient) => {
    if (!user) return;

    try {
      setAddingToPantryIngredient(ingredient.name);
      const token = await user.getIdToken();

      const payload = {
        name: normalizeWithAliases(ingredient.name),
        displayName: createDisplayName(ingredient.name),
        type: 'staple',
        category: suggestPantryCategory(ingredient.name) as PantryCategory,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add staple');
      }

      // Refresh pantry items to update "In Pantry" badges
      const pantryResponse = await getPantryItems(token);
      setPantryItems(pantryResponse.items || []);
    } catch (error: any) {
      console.error('Error adding staple:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to add staple',
        color: 'red',
        autoClose: 4000,
      });
    } finally {
      setAddingToPantryIngredient(null);
    }
  };

  const handleAddAsIngredient = (ingredient: Ingredient) => {
    setSelectedIngredientForPantry(ingredient);
    setPantryDrawerOpened(true);
  };

  const handlePantryDrawerSave = async () => {
    setPantryDrawerOpened(false);
    setSelectedIngredientForPantry(null);

    // Refresh pantry items to update "In Pantry" badges
    if (user) {
      try {
        const token = await user.getIdToken();
        const pantryResponse = await getPantryItems(token);
        setPantryItems(pantryResponse.items || []);
      } catch (error) {
        console.warn('Failed to refresh pantry items:', error);
      }
    }
  };

  const isIngredientInFoodsToAvoid = (ingredientName: string): boolean =>
    isAlreadyAvoided(ingredientName, foodsToAvoid);

  const handleAddToFoodsToAvoid = async (ingredient: Ingredient) => {
    if (!user) return;

    try {
      setAddingToPantryIngredient(ingredient.name);
      const token = await user.getIdToken();

      // Shared with the swap sheet's "always avoid" opt-in, so both surfaces
      // dedupe and case terms the same way. Re-fetches the profile internally;
      // a no-op when the ingredient is already on the list.
      const { dislikes } = await addFoodsToAvoid(token, [ingredient.name]);
      setFoodsToAvoid(dislikes);
    } catch (error: any) {
      console.error('Error adding to foods to avoid:', error);
    } finally {
      setAddingToPantryIngredient(null);
    }
  };

  const handlePantryDrawerDuplicateFound = (existingItem: any) => {
    modals.openConfirmModal({
      title: 'Item already in pantry',
      children: (
        <Text size="sm">
          <strong>{existingItem.displayName}</strong> is already in your pantry.
          <br /><br />
          Current quantity: {existingItem.quantity ? `${existingItem.quantity} ${existingItem.unit}` : 'Staple item'}
          <br /><br />
          Would you like to edit the existing item?
        </Text>
      ),
      labels: { confirm: 'Edit existing item', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: () => {
        setPantryDrawerOpened(false);
        setSelectedIngredientForPantry(null);
        notifications.show({
          title: 'Navigate to pantry',
          message: 'Go to my pantry to edit this item.',
          color: 'blue',
          autoClose: 4000,
        });
      },
      onCancel: () => {
        setPantryDrawerOpened(false);
        setSelectedIngredientForPantry(null);
      },
    });
  };

  // Fetch pantry items to show which ingredients are available
  useEffect(() => {
    const fetchPantryItems = async () => {
      if (!user) {
        setPantryLoading(false);
        return;
      }

      try {
        const token = await user.getIdToken();
        const response = await getPantryItems(token);
        setPantryItems(response.items || []);
      } catch (error) {
        console.warn('Failed to fetch pantry items:', error);
        setPantryItems([]);
      } finally {
        setPantryLoading(false);
      }
    };

    fetchPantryItems();
  }, [user]);

  // Fetch signed URLs for import images
  const fetchSignedUrls = async (gsUrls: string[]) => {
    if (!user || gsUrls.length === 0) return [];

    try {
      setLoadingSignedUrls(true);
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/import-images/signed-urls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageUrls: gsUrls }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch signed URLs');
      }

      const data = await response.json();
      return data.signedUrls || [];
    } catch (error) {
      console.error('Error fetching signed URLs:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load images. Please try again.',
        color: 'red',
      });
      return [];
    } finally {
      setLoadingSignedUrls(false);
    }
  };

  const handleStartEdit = () => {
    // Convert instructions to RecipeInstruction format for editing
    const editableInstructions = recipe.instructions?.map(instruction => {
      if (typeof instruction === 'string') {
        return { text: instruction, ingredients: [] };
      }
      return instruction;
    }) || [];
    
    const editableRecipe = {
      ...recipe,
      instructions: editableInstructions
    };

    setEditedRecipe(editableRecipe);
    instructionsRef.current = editableInstructions as RecipeInstruction[];
    // Create a copy to prevent mutations from affecting the original recipe
    ingredientsRef.current = (recipe.ingredients || []).map(ing => ({ ...ing }));
    setIsEditing(true);
    setValidationErrors([]);
  };

  const handleCancelEdit = () => {
    // For new recipes, navigate back instead of staying on the page
    if (recipe.id === 'new') {
      onBack();
      return;
    }

    // Reset editedRecipe to original recipe
    setEditedRecipe(recipe);

    // Reset refs to original recipe data to prevent mutations from persisting
    instructionsRef.current = (recipe.instructions as RecipeInstruction[] || []).map(inst => ({ ...inst }));
    ingredientsRef.current = (recipe.ingredients || []).map(ing => ({ ...ing }));

    setIsEditing(false);
    setValidationErrors([]);
    onExitEditMode?.();
  };


  const validateRecipe = (recipe: Recipe): string[] => {
    const errors: string[] = [];
    
    if (recipe.name.trim().length === 0) {
      errors.push('Recipe name is required');
    }
    
    const validIngredients = recipe.ingredients?.filter(ingredient => 
      ingredient.name.trim().length > 0 && 
      ingredient.unit.trim().length > 0 && 
      ingredient.quantity > 0
    ) || [];
    
    if (validIngredients.length === 0) {
      errors.push('Recipe must have at least one complete ingredient');
    }
    
    const validInstructions = recipe.instructions ? recipe.instructions.filter(instruction => instruction.text.trim().length > 0) : [];
    
    if (validInstructions.length === 0) {
      errors.push('Recipe must have at least one instruction');
    }
    
    return errors;
  };

  const cleanRecipe = (recipe: Recipe): Recipe => {
    const cleanedIngredients = recipe.ingredients?.filter(ingredient => 
      ingredient.name.trim().length > 0 && 
      ingredient.unit.trim().length > 0 && 
      ingredient.quantity > 0
    ) || [];
    
    const validIngredientNames = new Set(cleanedIngredients.map(ing => ing.name));
    
    const cleanedInstructions = recipe.instructions ? 
      recipe.instructions
        .filter(instruction => instruction.text.trim().length > 0)
        .map(instruction => ({
          ...instruction,
          ingredients: instruction.ingredients?.filter(ingredientName => 
            validIngredientNames.has(ingredientName)
          ) || []
        })) : [];

    return {
      ...recipe,
      ingredients: cleanedIngredients,
      instructions: cleanedInstructions
    };
  };

  const handleSaveEdit = async () => {
    if (!onSaveRecipe) return;

    const recipeWithNotesAndInstructions = { 
      ...editedRecipe, 
      description: currentDescription,
      cuisine: currentCuisine,
      notes: currentNotes,
      instructions: instructionsRef.current,
      ingredients: ingredientsRef.current
    };
    const errors = validateRecipe(recipeWithNotesAndInstructions);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    try {
      setIsSaving(true);
      setValidationErrors([]);
      const cleanedRecipe = cleanRecipe(recipeWithNotesAndInstructions);
      await onSaveRecipe(cleanedRecipe);
      setIsEditing(false);
      onExitEditMode?.();
    } catch (error: any) {
      console.error('Failed to save recipe:', error);
      // Extract error message from API response
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save recipe. Please try again.';
      setValidationErrors([errorMessage]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddToInstacart = async () => {
    if (recipe.id === 'new') return;

    // Open a blank tab immediately to avoid popup blocker.
    // We use about:blank instead of /instacart-redirect.html because the backend
    // serves recipe pages with Cross-Origin-Opener-Policy headers that sever the
    // window.open reference, preventing the parent from redirecting the tab.
    const newWindow = window.open('about:blank', '_blank');
    if (newWindow) {
      // Fetch the static redirect page and write it into the blank tab
      fetch('/instacart-redirect.html')
        .then(res => res.text())
        .then(html => {
          if (!newWindow.closed) {
            newWindow.document.write(html);
            newWindow.document.close();
          }
        })
        .catch(() => {}); // Loading page is cosmetic — OK to fail silently
    }

    setAddingToInstacart(true);

    try {
      let result: { instacartUrl: string };

      // Check if this is a scaled recipe (has originalServings) and we have meal plan context
      const isScaledRecipe = recipe.originalServings !== undefined;
      const hasCompleteContext = isScaledRecipe && mealPlanId && assignmentId && user;

      if (hasCompleteContext) {
        // For scaled recipes with meal plan context, use assignment endpoint
        result = await createInstacartRecipePageForAssignment(mealPlanId!, assignmentId!, await user!.getIdToken());
      } else {
        // For canonical recipes or scaled recipes without context, use canonical endpoint
        if (isScaledRecipe && (!mealPlanId || !assignmentId)) {
          console.warn('Scaled recipe detected but missing meal plan context. Using canonical recipe endpoint.');
        }
        const token = user ? await user.getIdToken() : undefined;
        const recipeOwnerId = ownerId || user?.uid;
        if (!recipeOwnerId) {
          throw new Error('Unable to determine recipe owner');
        }
        result = await createInstacartRecipePage(recipeOwnerId, recipe.id, token);
      }
      
      // Navigate the already-opened tab to Instacart
      if (newWindow && !newWindow.closed) {
        newWindow.location.href = result.instacartUrl;
      } else {
        // Fallback if tab opening failed or was closed
        window.open(result.instacartUrl, '_blank');
      }
      
    } catch (error) {
      console.error('Error creating Instacart recipe page:', error);
      
      // Close the progress window on error
      if (newWindow) {
        newWindow.close();
      }
      
      notifications.show({
        title: 'Error',
        message: 'Failed to create Instacart recipe page. Please try again.',
        color: 'red'
      });
    } finally {
      setAddingToInstacart(false);
    }
  };

  // Owners can share once their profile is public; non-owners are already
  // viewing a public recipe, so they can always share.
  const handleShare = () => {
    if (profilePublic || !isOwner) {
      void shareLink({
        title: recipe.name,
        url: `${window.location.origin}${buildRecipePath(recipe, ownerId || '')}`,
        copyMessage: 'Recipe link copied to clipboard',
      });
    } else {
      // Profile is not public — take the owner to privacy settings. This is a
      // menu-item navigation, so close the menu instantly and let a frame
      // paint first (see utils/afterPaint).
      setInstantMenuClose(true);
      setContextMenuOpen(false);
      afterPaint(() => {
        navigate('/profile?section=privacy');
        setInstantMenuClose(false);
      });
    }
  };

  // Helper function to prioritize and limit dietary restrictions
  const getPrioritizedDietaryRestrictions = (recipeDietaryRestrictions: string[] | undefined, maxVisible: number = 3) => {
    if (!recipeDietaryRestrictions || recipeDietaryRestrictions.length === 0) {
      return { visible: [], remaining: [] };
    }

    // If user restrictions haven't loaded yet, return empty to trigger skeleton display
    if (userDietaryRestrictions === null) {
      return { visible: [], remaining: [] };
    }

    const prioritized = getPrioritizedRestrictions(recipeDietaryRestrictions, {
      dietaryRestrictions: userDietaryRestrictions,
      householdSegment: userHouseholdSegment,
    });

    // Split into visible and remaining
    const visible = prioritized.slice(0, maxVisible);
    const remaining = prioritized.slice(maxVisible);

    return { visible, remaining };
  };

  const theme = useMantineTheme();
  const prepTimeHandlersRef = useRef<NumberInputHandlers>(null);
  const cookTimeHandlersRef = useRef<NumberInputHandlers>(null);
  const servingsHandlersRef = useRef<NumberInputHandlers>(null);
  return (
    <>
    <MobileOptimized>
      <Stack gap="lg">
        {/* Header with Back Button */}
        <Group align="flex-start" wrap="nowrap">
          <ActionIcon
            variant="subtle"
            onClick={onBack}
            size="lg"
            style={{ marginTop: 4 }}
          >
            <IconChevronLeft size={20} />
          </ActionIcon>
          {/* Title/actions share one block-flow container (not a flex row) so the
              recipe name can wrap beneath the kebab menu. The kebab is absolutely
              positioned in the top-right (below) and a float spacer reserves that
              corner so the name's first line(s) wrap around it and later lines
              reclaim the full width. */}
          <Box style={{ flex: 1, minWidth: 0, position: 'relative', minHeight: rem(42) }}>
            {!isEditing && (
              <Box
                aria-hidden
                style={{ float: 'right', width: rem(42), height: rem(42), marginLeft: 'var(--mantine-spacing-sm)' }}
              />
            )}
            {isEditing ? (
              <TextInput
                value={editedRecipe.name}
                onChange={(event) => {
                  const value = event?.currentTarget?.value || '';
                  if (value.length <= 100) { // Limit recipe name to 100 characters
                    setEditedRecipe(prev => ({ ...prev, name: value }));
                  }
                }}
                placeholder={recipe.id === 'new' ? "Recipe name..." : undefined}
                style={{ width: '100%' }}
                variant="unstyled"
                maxLength={100}
                styles={{
                  input: {
                    fontSize: isMobile ? theme.headings.sizes.h3.fontSize : theme.headings.sizes.h2.fontSize,
                    fontWeight: isMobile ? 600 : 700,
                    lineHeight: isMobile ? theme.headings.sizes.h3.lineHeight : theme.headings.sizes.h2.lineHeight,
                    padding: 0,
                    paddingRight: rem(96), // leave room for the save/cancel controls
                    border: 0,
                  }
                }}
              />
            ) : (
              <>
                <Title order={2} visibleFrom="sm" style={{ display: 'inline' }}>
                  {recipe.name}
                </Title>
                <Title order={3} hiddenFrom="sm" style={{ display: 'inline' }}>
                  {recipe.name}
                </Title>
                {/* The badges sit inline after the name, so `vertical-align: middle`
                    resolves against the surrounding line's font. Without this
                    wrapper that's the container's 16px body font, which parks the
                    badges well below the heading's text center; carrying the h2
                    metrics here centers them on the name instead. The small lift
                    moves that from x-height center to cap-height center, which is
                    what reads as centered next to a Title Case name. */}
                <Box
                  component="span"
                  visibleFrom="sm"
                  style={{
                    fontSize: 'var(--mantine-h2-font-size)',
                    lineHeight: 'var(--mantine-h2-line-height)',
                    position: 'relative',
                    top: '-0.08em',
                  }}
                >
                  {recipe.importStatus?.status === 'needs_review' && (
                    <Tooltip
                      label="This recipe was imported but needs manual review and editing to ensure accuracy before saving"
                      multiline
                      w={220}
                      withArrow
                      events={{ hover: true, focus: true, touch: true }}
                    >
                      <Badge size="sm" variant="light" color="orange" ml="sm" style={{ verticalAlign: 'middle' }}>
                        Needs Review
                      </Badge>
                    </Tooltip>
                  )}
                  {cookHistory && cookHistory.cookCount > 0 && (
                    <Tooltip
                      label={`Made ${cookHistory.cookCount} ${cookHistory.cookCount === 1 ? 'time' : 'times'} this year${cookHistory.lastCookedAt ? ` · Last made ${formatTimeAgo(cookHistory.lastCookedAt).toLowerCase()}` : ''}`}
                      withArrow
                      events={{ hover: true, focus: true, touch: true }}
                    >
                      <Badge size="sm" variant="light" color="gray" ml="sm" style={{ verticalAlign: 'middle' }}>
                        Made {cookHistory.cookCount}x
                      </Badge>
                    </Tooltip>
                  )}
                </Box>
                {(recipe.importStatus?.status === 'needs_review' || (cookHistory && cookHistory.cookCount > 0)) && (
                  <Box hiddenFrom="sm" style={{ paddingTop: rem(8) }}>
                    <Group gap="xs">
                      {recipe.importStatus?.status === 'needs_review' && (
                        <Tooltip
                          label="This recipe was imported but needs manual review and editing to ensure accuracy before saving"
                          multiline
                          w={220}
                          withArrow
                          events={{ hover: true, focus: true, touch: true }}
                        >
                          <Badge size="sm" variant="light" color="orange" style={{ width: 'fit-content' }}>
                            Needs Review
                          </Badge>
                        </Tooltip>
                      )}
                      {cookHistory && cookHistory.cookCount > 0 && (
                        <Tooltip
                          label={`Made ${cookHistory.cookCount} ${cookHistory.cookCount === 1 ? 'time' : 'times'} this year${cookHistory.lastCookedAt ? ` · Last made ${formatTimeAgo(cookHistory.lastCookedAt).toLowerCase()}` : ''}`}
                          withArrow
                          events={{ hover: true, focus: true, touch: true }}
                        >
                          <Badge size="sm" variant="light" color="gray" style={{ width: 'fit-content' }}>
                            Made {cookHistory.cookCount}x
                          </Badge>
                        </Tooltip>
                      )}
                    </Group>
                  </Box>
                )}
              </>
            )}
          {/* Context menu for all actions — absolutely positioned in the top-right
              corner so the recipe name above can wrap full-width beneath it. */}
          <Box style={{ position: 'absolute', top: 0, right: 0 }}>
          <Group gap="xs">
            {!isEditing ? (
              <Menu transitionProps={instantMenuClose ? { duration: 0 } : { transition: 'pop' }} position="bottom-end" withinPortal opened={contextMenuOpen} onChange={setContextMenuOpen}>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                  >
                    <IconDotsVertical size={20} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {!isCatalogView && isOwner && canEdit && !isScaledRecipe && (
                    <Menu.Item
                      leftSection={<IconEdit size={18} stroke={1.5} />}
                      onClick={handleStartEdit}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      Edit
                    </Menu.Item>
                  )}
                  {!isCatalogView && (
                    <Menu.Item
                      leftSection={<IconPrinter size={18} stroke={1.5} />}
                      onClick={() => printRecipe(recipe)}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      Print
                    </Menu.Item>
                  )}
                  {!isCatalogView && isOwner && recipe.importStatus?.status === 'needs_review' ? (
                    <Menu.Item
                      leftSection={<IconCheck size={18} stroke={1.5} />}
                      color="green"
                      onClick={handleToggleCookbook}
                      disabled={isSavingCookbook}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      Confirm recipe
                    </Menu.Item>
                  ) : !isCatalogView && isOwner && (
                    <Menu.Item
                      leftSection={localIsSaved ? <IconBookmarkOff size={18} stroke={1.5} /> : <IconBookmark size={18} stroke={1.5} />}
                      color={localIsSaved ? 'orange' : 'green'}
                      onClick={handleToggleCookbook}
                      disabled={isSavingCookbook}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      {localIsSaved ? 'Remove from cookbook' : 'Add to cookbook'}
                    </Menu.Item>
                  )}
                  {!isCatalogView && isOwner && !isScaledRecipe && !assignmentId && (
                    <Menu.Item
                      leftSection={<IconTrash size={18} stroke={1.5} />}
                      color="red"
                      onClick={handleDeleteRecipe}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      Delete recipe
                    </Menu.Item>
                  )}
                  {!isCatalogView && isOwner && (
                    <Menu.Item
                      leftSection={<IconShare size={18} stroke={1.5} />}
                      onClick={handleShare}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      {profilePublic ? 'Share' : 'Enable sharing'}
                    </Menu.Item>
                  )}
                  {!isOwner && !isCatalogView && (
                    <Menu.Item
                      leftSection={<IconShare size={18} stroke={1.5} />}
                      onClick={handleShare}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      Share
                    </Menu.Item>
                  )}
                  {!isCatalogView && isOwner && recipe.importStatus?.sourceUrl && (
                    <Menu.Item
                      leftSection={<IconExternalLink size={18} stroke={1.5} />}
                      onClick={() => window.open(recipe.importStatus!.sourceUrl!, '_blank')}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      View original recipe
                    </Menu.Item>
                  )}
                  {!isCatalogView && recipe.importStatus?.pinterestPinUrl && (
                    <Menu.Item
                      leftSection={<IconBrandPinterest size={18} stroke={1.5} />}
                      onClick={() => window.open(recipe.importStatus!.pinterestPinUrl!, '_blank')}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      View on Pinterest
                    </Menu.Item>
                  )}
                  {!isCatalogView && isOwner && recipe.importStatus?.imageUrls && recipe.importStatus.imageUrls.length > 0 && (
                    <Menu.Item
                      leftSection={<IconPhoto size={18} stroke={1.5} />}
                      onClick={async () => {
                        const urls = await fetchSignedUrls(recipe.importStatus!.imageUrls!);
                        if (urls.length > 0) {
                          setSignedImageUrls(urls);
                          setSelectedImageIndex(0);
                          setImageModalOpened(true);
                        }
                      }}
                      disabled={loadingSignedUrls}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      View original images ({recipe.importStatus.imageUrls.length})
                    </Menu.Item>
                  )}
                  {!isCatalogView && isOwner && recipe.origin !== 'user_recipe' && (
                    <Group gap="xs" px={isMobile ? 16 : 12} pt={isMobile ? 8 : 6} pb={isMobile ? 8 : 6} justify="center">
                      <ActionIcon
                        variant={localRating === 'up' ? 'light' : 'default'}
                        color={localRating === 'up' ? 'green' : undefined}
                        size={isMobile ? 'lg' : 'md'}
                        loading={isRating && localRating === 'up'}
                        disabled={localRating === 'down'}
                        onClick={localRating === 'up' ? handleUndoRating : handleRateUp}
                      >
                        {localRating === 'up' ? <IconThumbUpFilled size={16} /> : <IconThumbUp size={16} stroke={1.5} />}
                      </ActionIcon>
                      <ActionIcon
                        variant={localRating === 'down' ? 'light' : 'default'}
                        color={localRating === 'down' ? 'red' : undefined}
                        size={isMobile ? 'lg' : 'md'}
                        loading={isRating && localRating === 'down'}
                        disabled={localRating === 'up'}
                        onClick={localRating === 'down' ? handleUndoRating : handleRateDown}
                      >
                        {localRating === 'down' ? <IconThumbDownFilled size={16} /> : <IconThumbDown size={16} stroke={1.5} />}
                      </ActionIcon>
                    </Group>
                  )}
                  {isAdmin && (
                    <>
                      {!isCatalogView && <Divider my={4} />}
                      <Menu.Label>Admin</Menu.Label>
                      {isCatalogView && canEdit && (
                        <Menu.Item
                          leftSection={<IconEdit size={18} stroke={1.5} />}
                          onClick={handleStartEdit}
                          style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                        >
                          Edit
                        </Menu.Item>
                      )}
                      {!isCatalogView && (
                        <Menu.Item
                          leftSection={<IconFlame size={18} stroke={1.5} />}
                          onClick={async () => {
                            if (!user) return;
                            setTriggeringCook(true);
                            try {
                              const token = await user.getIdToken();
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/cook-event`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                                credentials: 'include',
                                body: JSON.stringify({
                                  recipeId: recipe.id,
                                  mealPlanId,
                                  stepsCompleted: recipe.instructions?.length || 0,
                                  totalSteps: recipe.instructions?.length || 0,
                                }),
                              });
                              if (!response.ok) throw new Error('Failed to trigger cook event');
                              notifications.show({
                                title: 'Cook event triggered',
                                message: `Cook event logged for "${recipe.name}"`,
                                color: 'green',
                                icon: <IconCheck size={18} />,
                                autoClose: 3000,
                              });
                            } catch (error) {
                              console.error('[Admin] Failed to trigger cook event:', error);
                              notifications.show({
                                title: 'Error',
                                message: 'Failed to trigger cook event',
                                color: 'red',
                                icon: <IconX size={18} />,
                                autoClose: 5000,
                              });
                            } finally {
                              setTriggeringCook(false);
                            }
                          }}
                          disabled={triggeringCook}
                          style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                        >
                          {triggeringCook ? 'Triggering...' : 'Trigger cook event'}
                        </Menu.Item>
                      )}
                      <Menu.Item
                        leftSection={<IconPhoto size={18} stroke={1.5} />}
                        onClick={async () => {
                          if (!user) return;
                          setRegeneratingImage(true);
                          try {
                            const token = await user.getIdToken();
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recipes/regenerate-image`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              credentials: 'include',
                              body: JSON.stringify(
                                isCatalogView
                                  ? { catalogRecipeId: recipe.id }
                                  : { recipeId: recipe.id, userId: ownerId }
                              ),
                            });
                            if (!response.ok) {
                              const data = await response.json().catch(() => ({}));
                              throw new Error(data.error || 'Failed to regenerate image');
                            }
                            const data = await response.json();
                            setEditedRecipe(prev => ({ ...prev, imageUrl: data.imageUrl }));
                            notifications.show({
                              title: 'Image regenerated',
                              message: `New image generated for "${recipe.name}"`,
                              color: 'green',
                              icon: <IconCheck size={18} />,
                              autoClose: 3000,
                            });
                          } catch (error: any) {
                            console.error('[Admin] Failed to regenerate image:', error);
                            notifications.show({
                              title: 'Error',
                              message: error.message || 'Failed to regenerate image',
                              color: 'red',
                              icon: <IconX size={18} />,
                              autoClose: 5000,
                            });
                          } finally {
                            setRegeneratingImage(false);
                          }
                        }}
                        disabled={regeneratingImage}
                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                      >
                        {regeneratingImage ? 'Regenerating...' : 'Regenerate image'}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconChartBar size={18} stroke={1.5} />}
                        onClick={async () => {
                          if (!user) return;
                          setRefreshingNutrition(true);
                          try {
                            const token = await user.getIdToken();
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recipes/refresh-nutrition`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              credentials: 'include',
                              body: JSON.stringify(
                                isCatalogView
                                  ? { catalogRecipeId: recipe.id }
                                  : { recipeId: recipe.id, userId: ownerId }
                              ),
                            });
                            if (!response.ok) {
                              const data = await response.json().catch(() => ({}));
                              throw new Error(data.error || 'Failed to refresh nutrition');
                            }
                            const data = await response.json();
                            setEditedRecipe(prev => ({ ...prev, nutrition: data.nutrition }));
                            notifications.show({
                              title: 'Nutrition refreshed',
                              message: `${data.source === 'database' ? 'From the ingredient database' : 'AI estimate (incomplete ingredient coverage)'}${data.catalogUpdated ? ' · catalog updated too' : ''}`,
                              color: 'green',
                              icon: <IconCheck size={18} />,
                              autoClose: 4000,
                            });
                          } catch (error: any) {
                            console.error('[Admin] Failed to refresh nutrition:', error);
                            notifications.show({
                              title: 'Error',
                              message: error.message || 'Failed to refresh nutrition',
                              color: 'red',
                              icon: <IconX size={18} />,
                              autoClose: 5000,
                            });
                          } finally {
                            setRefreshingNutrition(false);
                          }
                        }}
                        disabled={refreshingNutrition}
                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                      >
                        {refreshingNutrition ? 'Refreshing...' : 'Refresh nutrition'}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconReportMoney size={18} stroke={1.5} />}
                        onClick={async () => {
                          if (!user) return;
                          setLoadingCostBreakdown(true);
                          try {
                            const token = await user.getIdToken();
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recipes/cost-breakdown`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              credentials: 'include',
                              body: JSON.stringify(
                                isCatalogView
                                  ? { catalogRecipeId: recipe.id }
                                  : { recipeId: recipe.id, userId: ownerId }
                              ),
                            });
                            if (!response.ok) {
                              const data = await response.json().catch(() => ({}));
                              throw new Error(data.error || 'Failed to compute cost breakdown');
                            }
                            const data = await response.json();
                            modals.open({
                              modalId: 'cost-breakdown',
                              title: <Text fw={600}>Cost breakdown — {recipe.name}</Text>,
                              size: 'xl',
                              children: <CostBreakdownContent breakdown={data.breakdown as RecipeCostBreakdown} />,
                            });
                          } catch (error: any) {
                            console.error('[Admin] Failed to compute cost breakdown:', error);
                            notifications.show({
                              title: 'Error',
                              message: error.message || 'Failed to compute cost breakdown',
                              color: 'red',
                              icon: <IconX size={18} />,
                              autoClose: 5000,
                            });
                          } finally {
                            setLoadingCostBreakdown(false);
                          }
                        }}
                        disabled={loadingCostBreakdown}
                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                      >
                        {loadingCostBreakdown ? 'Computing...' : 'View cost breakdown'}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconRefresh size={18} stroke={1.5} />}
                        onClick={async () => {
                          if (!user) return;
                          setRefreshingCost(true);
                          try {
                            const token = await user.getIdToken();
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recipes/refresh-cost`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              credentials: 'include',
                              body: JSON.stringify(
                                isCatalogView
                                  ? { catalogRecipeId: recipe.id }
                                  : { recipeId: recipe.id, userId: ownerId }
                              ),
                            });
                            if (!response.ok) {
                              const data = await response.json().catch(() => ({}));
                              throw new Error(data.error || 'Failed to refresh pricing');
                            }
                            const data = await response.json();
                            if (data.status === 'recomputed' && data.costEstimate) {
                              setEditedRecipe(prev => ({ ...prev, costEstimate: data.costEstimate }));
                              notifications.show({
                                title: 'Pricing refreshed',
                                message: `~${fmtUsd(data.costEstimate.perServing.mid)}/serving${data.catalogUpdated ? ' (catalog updated too)' : ''}`,
                                color: 'green',
                                icon: <IconCheck size={18} />,
                                autoClose: 3000,
                              });
                            } else {
                              notifications.show({
                                title: 'Pricing not refreshed',
                                message: 'Incomplete ingredient coverage — the existing estimate was kept.',
                                color: 'yellow',
                                icon: <IconAlertTriangle size={18} />,
                                autoClose: 5000,
                              });
                            }
                          } catch (error: any) {
                            console.error('[Admin] Failed to refresh pricing:', error);
                            notifications.show({
                              title: 'Error',
                              message: error.message || 'Failed to refresh pricing',
                              color: 'red',
                              icon: <IconX size={18} />,
                              autoClose: 5000,
                            });
                          } finally {
                            setRefreshingCost(false);
                          }
                        }}
                        disabled={refreshingCost}
                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                      >
                        {refreshingCost ? 'Refreshing...' : 'Refresh pricing'}
                      </Menu.Item>
                      {!editedRecipe.isCurated ? (
                        <Menu.Item
                          leftSection={<IconStar size={18} stroke={1.5} />}
                          disabled={curateLoading}
                          onClick={async () => {
                            if (!user || (!ownerId && !isCatalogView)) return;

                            const executeCurate = async (action: 'curate' | 'take', confirmed?: boolean) => {
                              setCurateLoading(true);
                              try {
                                const token = await user.getIdToken();
                                const body = isCatalogView
                                  ? { catalogRecipeId: recipe.id, action, confirmed }
                                  : { userId: ownerId, recipeId: recipe.id, action, confirmed };
                                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recipes/curate`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                  credentials: 'include',
                                  body: JSON.stringify(body),
                                });
                                if (!response.ok) {
                                  const data = await response.json().catch(() => ({}));
                                  throw new Error(data.error || 'Failed to curate recipe');
                                }
                                const data = await response.json();

                                // Server detected version differences — prompt admin
                                if (data.status === 'confirm_needed') {
                                  setCurateLoading(false);
                                  type DiffChange = { type: 'added' | 'removed' | 'changed'; label: string; userValue?: string; catalogValue?: string };
                                  type DiffItem = { field: string; userValue?: string; catalogValue?: string; changes?: DiffChange[] };
                                  const diffs = data.diffs as DiffItem[];
                                  modals.open({
                                    modalId: 'curate-version-choice',
                                    title: <Text fw={600}>Recipe versions differ</Text>,
                                    size: 'lg',
                                    children: (
                                      <Stack gap="md">
                                        <Text size="sm">
                                          This user's version of <strong>{recipe.name}</strong> differs from the catalog version.
                                        </Text>
                                        <Stack gap="xs">
                                          {diffs.filter((d) => !d.changes || d.changes.length > 0).map((diff) => (
                                            <Box key={diff.field} p="xs" style={{ borderRadius: 8, border: '1px solid var(--mantine-color-gray-3)' }}>
                                              <Text size="xs" fw={600} c="dimmed" mb={4}>{diff.field}</Text>
                                              {diff.changes ? (
                                                <Stack gap={2}>
                                                  {diff.changes.map((change, i) => (
                                                    <Box key={i}>
                                                      {change.type === 'added' && (
                                                        <Box px="xs" py={2} style={{ borderRadius: 4, backgroundColor: 'var(--mantine-color-green-0)' }}>
                                                          <Text size="sm" c="green.8" style={{ fontFamily: 'monospace' }}>+ {change.userValue || change.label}</Text>
                                                        </Box>
                                                      )}
                                                      {change.type === 'removed' && (
                                                        <Box px="xs" py={2} style={{ borderRadius: 4, backgroundColor: 'var(--mantine-color-red-0)' }}>
                                                          <Text size="sm" c="red.8" style={{ fontFamily: 'monospace' }}>- {change.catalogValue || change.label}</Text>
                                                        </Box>
                                                      )}
                                                      {change.type === 'changed' && (() => {
                                                        const parts = diffWords(change.catalogValue || '', change.userValue || '');
                                                        return (
                                                          <>
                                                            <Box px="xs" py={2} style={{ borderRadius: '4px 4px 0 0', backgroundColor: 'var(--mantine-color-red-0)' }}>
                                                              <Text size="sm" c="red.8" style={{ fontFamily: 'monospace' }}>- {parts.map((p, j) =>
                                                                p.added ? null : <span key={j} style={p.removed ? { backgroundColor: 'var(--mantine-color-red-2)', borderRadius: 2 } : undefined}>{p.value}</span>
                                                              )}</Text>
                                                            </Box>
                                                            <Box px="xs" py={2} style={{ borderRadius: '0 0 4px 4px', backgroundColor: 'var(--mantine-color-green-0)' }}>
                                                              <Text size="sm" c="green.8" style={{ fontFamily: 'monospace' }}>+ {parts.map((p, j) =>
                                                                p.removed ? null : <span key={j} style={p.added ? { backgroundColor: 'var(--mantine-color-green-2)', borderRadius: 2 } : undefined}>{p.value}</span>
                                                              )}</Text>
                                                            </Box>
                                                          </>
                                                        );
                                                      })()}
                                                    </Box>
                                                  ))}
                                                </Stack>
                                              ) : (() => {
                                                const parts = diffWords(diff.catalogValue || '(empty)', diff.userValue || '(empty)');
                                                return (
                                                  <Stack gap={2}>
                                                    <Box px="xs" py={2} style={{ borderRadius: 4, backgroundColor: 'var(--mantine-color-red-0)' }}>
                                                      <Text size="sm" c="red.8" style={{ fontFamily: 'monospace' }}>- {parts.map((p, j) =>
                                                        p.added ? null : <span key={j} style={p.removed ? { backgroundColor: 'var(--mantine-color-red-2)', borderRadius: 2 } : undefined}>{p.value}</span>
                                                      )}</Text>
                                                    </Box>
                                                    <Box px="xs" py={2} style={{ borderRadius: 4, backgroundColor: 'var(--mantine-color-green-0)' }}>
                                                      <Text size="sm" c="green.8" style={{ fontFamily: 'monospace' }}>+ {parts.map((p, j) =>
                                                        p.removed ? null : <span key={j} style={p.added ? { backgroundColor: 'var(--mantine-color-green-2)', borderRadius: 2 } : undefined}>{p.value}</span>
                                                      )}</Text>
                                                    </Box>
                                                  </Stack>
                                                );
                                              })()}
                                            </Box>
                                          ))}
                                        </Stack>
                                        <Text size="sm">Which version should be curated?</Text>
                                        <Group justify="flex-end" gap="sm">
                                          <Button variant="default" onClick={() => {
                                            modals.close('curate-version-choice');
                                            executeCurate('curate', true);
                                          }}>
                                            Keep catalog version
                                          </Button>
                                          <Button color="orange" onClick={() => {
                                            modals.close('curate-version-choice');
                                            executeCurate('take');
                                          }}>
                                            Take user's version
                                          </Button>
                                        </Group>
                                      </Stack>
                                    ),
                                  });
                                  return;
                                }

                                setEditedRecipe(prev => ({ ...prev, isCurated: true, catalogRecipeId: data.catalogRecipeId }));
                                notifications.show({ title: 'Recipe curated', message: `"${recipe.name}" is now a Top pick`, color: 'orange', icon: <IconStar size={18} />, autoClose: 3000 });
                              } catch (error: any) {
                                notifications.show({ title: 'Error', message: error.message || 'Failed to curate', color: 'red', icon: <IconX size={18} />, autoClose: 5000 });
                              } finally {
                                setCurateLoading(false);
                              }
                            };

                            executeCurate('curate');
                          }}
                          style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                        >
                          {curateLoading ? 'Curating...' : 'Curate recipe'}
                        </Menu.Item>
                      ) : (
                        <Menu.Item
                          leftSection={<IconStar size={18} stroke={1.5} />}
                          disabled={curateLoading}
                          onClick={async () => {
                            if (!user || (!ownerId && !isCatalogView)) return;
                            setCurateLoading(true);
                            try {
                              const token = await user.getIdToken();
                              const body = isCatalogView
                                ? { catalogRecipeId: recipe.id, action: 'uncurate' }
                                : { userId: ownerId, recipeId: recipe.id, action: 'uncurate' };
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recipes/curate`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                credentials: 'include',
                                body: JSON.stringify(body),
                              });
                              if (!response.ok) {
                                const data = await response.json().catch(() => ({}));
                                throw new Error(data.error || 'Failed to uncurate recipe');
                              }
                              setEditedRecipe(prev => ({ ...prev, isCurated: undefined }));
                              notifications.show({ title: 'Recipe uncurated', message: `"${recipe.name}" is no longer a Top pick`, color: 'gray', icon: <IconCheck size={18} />, autoClose: 3000 });
                            } catch (error: any) {
                              notifications.show({ title: 'Error', message: error.message || 'Failed to uncurate', color: 'red', icon: <IconX size={18} />, autoClose: 5000 });
                            } finally {
                              setCurateLoading(false);
                            }
                          }}
                          style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                        >
                          {curateLoading ? 'Uncurating...' : 'Uncurate recipe'}
                        </Menu.Item>
                      )}
                      {!isCatalogView && recipe.catalogRecipeId && (
                        <Menu.Item
                          leftSection={<IconExternalLink size={18} stroke={1.5} />}
                          onClick={() => window.open(`/admin/catalog-recipe/${recipe.catalogRecipeId}`, '_blank')}
                          style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                        >
                          View catalog version
                        </Menu.Item>
                      )}
                      {isCatalogView && (
                        <Menu.Item
                          leftSection={<IconTrash size={18} stroke={1.5} />}
                          color="red"
                          onClick={() => {
                            setContextMenuOpen(false);
                            modals.openConfirmModal({
                              title: 'Delete catalog recipe',
                              children: (
                                <Text size="sm">
                                  Are you sure you want to delete <strong>{recipe.name}</strong> from the catalog? Users who already have this recipe will keep their copies.
                                </Text>
                              ),
                              labels: { confirm: 'Delete', cancel: 'Cancel' },
                              confirmProps: { color: 'red' },
                              onConfirm: async () => {
                                if (!user) return;
                                try {
                                  const token = await user.getIdToken();
                                  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/catalog-recipes/${recipe.id}`, {
                                    method: 'DELETE',
                                    headers: { Authorization: `Bearer ${token}` },
                                  });
                                  if (!response.ok) {
                                    const data = await response.json().catch(() => ({}));
                                    throw new Error(data.error || 'Failed to delete catalog recipe');
                                  }
                                  notifications.show({
                                    title: 'Recipe deleted',
                                    message: `"${recipe.name}" has been removed from the catalog`,
                                    color: 'green',
                                    icon: <IconCheck size={18} />,
                                    autoClose: 3000,
                                  });
                                  onBack();
                                } catch (error: any) {
                                  notifications.show({
                                    title: 'Error',
                                    message: error.message || 'Failed to delete catalog recipe',
                                    color: 'red',
                                    icon: <IconX size={18} />,
                                    autoClose: 5000,
                                  });
                                }
                              },
                            });
                          }}
                          style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                        >
                          Delete from catalog
                        </Menu.Item>
                      )}
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            ) : (
              <Group gap="xs">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={handleCancelEdit}
                  size="lg"
                  disabled={isSaving}
                >
                  <IconX size={20} />
                </ActionIcon>
                <ActionIcon
                  variant="filled"
                  color="blue"
                  onClick={handleSaveEdit}
                  size="lg"
                  loading={isSaving}
                >
                  <IconCheck size={20} />
                </ActionIcon>
              </Group>
            )}
          </Group>
          </Box>
          </Box>
        </Group>

        {/* Recipe Attribution for non-owners */}
        {!isOwner && owner && (
          <Paper
            px="md"
            py={14}
            radius="lg"
            withBorder
            style={{ cursor: ownerTasteProfile?.personalityBlurb ? 'pointer' : 'default', backgroundColor: 'var(--mantine-color-gray-0)' }}
            onClick={() => {
              if (ownerTasteProfile?.personalityBlurb) {
                setOwnerBioExpanded(!ownerBioExpanded);
              }
            }}
          >
            <Stack gap={0}>
              <Group gap="sm" align="center" wrap="nowrap">
                <UserAvatar
                  photoURL={owner.photoURL}
                  displayName={owner.displayName}
                  size="md"
                  style={{ opacity: 0, animation: 'avatarFadeIn 0.3s ease forwards', flexShrink: 0, cursor: 'pointer', ...(ownerTasteProfile?.personalityName ? { border: '2px solid var(--mantine-color-violet-4)' } : {}) }}
                  onClick={(e) => { if (ownerId) { e.stopPropagation(); navigate(`/u/${ownerId}`); } }}
                />
                <style>{`@keyframes avatarFadeIn { to { opacity: 1 } }`}</style>
                <Box style={{ flex: 1 }}>
                  <Text
                    fw={500}
                    style={{ cursor: 'pointer', width: 'fit-content' }}
                    onClick={(e) => { if (ownerId) { e.stopPropagation(); navigate(`/u/${ownerId}`); } }}
                  >
                    {ownerDisplayName(owner.displayName)}
                  </Text>
                  {/* Desktop: recipe count · personality on one line */}
                  <Text
                    size="xs"
                    c="dimmed"
                    visibleFrom="sm"
                    style={{ cursor: 'pointer', width: 'fit-content' }}
                    onClick={(e) => { if (ownerId) { e.stopPropagation(); navigate(`/u/${ownerId}`); } }}
                  >
                    {[
                      ownerRecipeCount ? `${formatCount(ownerRecipeCount)} ${ownerRecipeCount === 1 ? 'recipe' : 'recipes'} shared` : null,
                      ownerTasteProfile?.personalityName ? <Text key="personality" component="span" c="violet.6" fw={500} inherit>{ownerTasteProfile.personalityName}</Text> : null,
                    ].filter(Boolean).reduce((acc: React.ReactNode[], item, i) => {
                      if (i > 0) acc.push(' · ');
                      acc.push(item);
                      return acc;
                    }, [] as React.ReactNode[]) || 'Recipe by'}
                  </Text>
                  {/* Mobile: recipe count and personality on separate lines */}
                  <Box
                    hiddenFrom="sm"
                    style={{ cursor: 'pointer', width: 'fit-content' }}
                    onClick={(e) => { if (ownerId) { e.stopPropagation(); navigate(`/u/${ownerId}`); } }}
                  >
                    <Text size="xs" c="dimmed">
                      {ownerRecipeCount ? `${formatCount(ownerRecipeCount)} ${ownerRecipeCount === 1 ? 'recipe' : 'recipes'} shared` : 'Recipe by'}
                    </Text>
                    {ownerTasteProfile?.personalityName && (
                      <Text size="xs" c="violet.6" fw={500}>
                        {ownerTasteProfile.personalityName}
                      </Text>
                    )}
                  </Box>
                </Box>
                {ownerTasteProfile?.personalityBlurb ? (
                  <IconChevronDown
                    size={16}
                    color="var(--mantine-color-gray-5)"
                    style={{
                      flexShrink: 0,
                      transition: 'transform 200ms ease',
                      transform: ownerBioExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                ) : ownerId && (
                  <Text
                    size="sm"
                    c="blue"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                    onClick={(e) => { e.stopPropagation(); navigate(`/u/${ownerId}`); }}
                  >
                    View Profile
                  </Text>
                )}
              </Group>
              <Collapse in={ownerBioExpanded && !!ownerTasteProfile?.personalityBlurb}>
                <Box mt={8} ml={{ base: 0, sm: 50 }}>
                  <Text
                    size="xs"
                    c="dimmed"
                    lh={1.5}
                    pl="sm"
                    style={{
                      fontStyle: 'italic',
                      borderLeft: '3px solid var(--mantine-color-violet-3)',
                    }}
                  >
                    {ownerTasteProfile?.personalityBlurb}
                  </Text>
                  {ownerId && (
                    <Text
                      size="xs"
                      c="blue"
                      mt="sm"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/u/${ownerId}`); }}
                    >
                      View Profile
                    </Text>
                  )}
                </Box>
              </Collapse>
            </Stack>
          </Paper>
        )}

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Please fix the following issues:"
            color="red"
            variant="light"
            radius="lg"
          >
            <Stack gap="xs">
              {validationErrors.map((error, index) => (
                <Text key={index} size="sm">{error}</Text>
              ))}
            </Stack>
          </Alert>
        )}

        {/* Image and Metadata Side by Side */}
        <Flex gap="lg" direction={{ base: 'column', sm: 'row' }} align="stretch">
          {/* Recipe Image - show placeholder for new recipes */}
          <Box 
              style={{ 
                borderRadius: 8, 
                overflow: 'hidden',
                flexShrink: 0,
                position: 'relative',
                cursor: recipe.nutrition && !isTabletOrSmaller && !isEditing && !isScaledRecipe ? 'pointer' : 'default',
              }}
              w={{ base: '100%', sm: 300, lg: 400 }}
              h={{ base: 280, sm: 'auto' }}
              onMouseEnter={() => !isTabletOrSmaller && recipe.nutrition && !isScaledRecipe && setShowNutritionOverlay(true)}
              onMouseLeave={() => !isTabletOrSmaller && recipe.nutrition && !isScaledRecipe && setShowNutritionOverlay(false)}
              onClick={() => {
                if (recipe.nutrition && !isTabletOrSmaller && !isEditing && !isScaledRecipe) {
                  setNutritionDrawerOpened(true);
                }
              }}
            >
            {/* Absolute layer so the image fills the box without inflating its
                height — the row height is driven by the metadata column, and the
                image tracks it (cover-crops). */}
            <Box style={{ position: 'absolute', inset: 0 }}>
              <RecipeImage
                src={recipe.id !== 'new' ? (editedRecipe.imageUrl || recipe.imageUrl) : null}
                alt={recipe.name}
                width="100%"
                height="100%"
              />
            </Box>

            {/* Nutrition hover overlay for desktop - only show if nutrition data exists */}
            {recipe.nutrition && !isTabletOrSmaller && !isEditing && !isScaledRecipe && (
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.5) 40%, rgba(0, 0, 0, 0.25) 70%, transparent 100%)',
                      padding: '40px 12px 12px 12px',
                      opacity: showNutritionOverlay ? 1 : 0,
                      transition: 'opacity 0.2s ease',
                      pointerEvents: showNutritionOverlay ? 'auto' : 'none',
                      zIndex: 2,
                    }}
                  >
                    <Box
                      style={{ cursor: 'pointer' }}
                    >
                      <Text size="xs" fw={600} c="white" mb={4}>
                        Per Serving
                      </Text>
                      <Stack gap={2}>
                        <Text size="sm" c="white" style={{ lineHeight: 1.3 }}>
                          {recipe.nutrition.perServing.calories} cal • {recipe.nutrition.perServing.fat}g fat • {recipe.nutrition.perServing.sodium}mg sodium
                        </Text>
                        <Text size="sm" c="white" style={{ lineHeight: 1.3 }}>
                          {recipe.nutrition.perServing.carbohydrates}g carbs • {recipe.nutrition.perServing.fiber}g fiber • {recipe.nutrition.perServing.protein}g protein
                        </Text>
                      </Stack>
                    </Box>
                  </Box>
            )}
            
            {/* Cookbook ribbon for the user's saved recipes; Top pick only
                until the user adopts it (a saved top pick graduates). */}
            {!isEditing &&
              (!isCatalogView && isOwner && localIsSaved ? (
                <CookbookRibbon />
              ) : recipe.isCurated && !(isOwner && recipe.origin === 'user_recipe') ? (
                <TopPickRibbon />
              ) : null)}

            {/* Cuisine Badge/Editor Overlay */}
            {isEditing ? (
              <Box
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  zIndex: 1,
                  minWidth: 150
                }}
              >
                <CuisineEditor initialValue={editedRecipe.cuisine || ''} onCuisineChange={setCurrentCuisine} />
              </Box>
            ) : recipe.cuisine ? (
              <CuisineBadge
                cuisine={recipe.cuisine}
                placement="overlay"
                size="md"
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  zIndex: 1,
                  cursor: 'inherit'
                }}
              />
            ) : null}

            {/* Meals (slots) editor rides the image like the cuisine editor
                above: as a metadata-column row it inflated the column, and the
                image (which tracks the row height) with it. Drives the
                cookbook drawer's meal filters; system defaults fill until the
                user edits, then their verdict sticks. Flag-gated: meaningless
                in a dinner-only product. */}
            {isEditing && additionalMealsEnabled && (
              <Box
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                  // Same treatment as the nutrition overlay: on a dark scrim
                  // the default white unchecked chips and orange checked
                  // chips both read without any border surgery
                  background:
                    'linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%)',
                  padding: '32px 12px 12px 12px',
                }}
              >
                <Group gap="xs" align="center" wrap="wrap">
                  <Chip.Group
                    multiple
                    value={(['breakfast', 'lunch', 'dinner'] as const).filter(
                      (slot) =>
                        !!(editedRecipe.slots ?? deriveSlots(editedRecipe.category))[slot]
                    )}
                    onChange={(values) =>
                      setEditedRecipe(prev => ({
                        ...prev,
                        slots: {
                          breakfast: values.includes('breakfast'),
                          lunch: values.includes('lunch'),
                          dinner: values.includes('dinner'),
                        },
                      }))
                    }
                  >
                    <Group gap={6}>
                      <Chip value="breakfast" size="xs">Breakfast</Chip>
                      <Chip value="lunch" size="xs">Lunch</Chip>
                      <Chip value="dinner" size="xs">Dinner</Chip>
                    </Group>
                  </Chip.Group>
                </Group>
              </Box>
            )}

            {/* Scaled Recipe Indicator Overlay */}
            {isScaledRecipe && (
              <Paper
                p="sm"
                radius="lg"
                style={{
                  position: 'absolute',
                  bottom: 12,
                  left: 12,
                  right: 12,
                  zIndex: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(4px)'
                }}
              >
                <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
                  <Text size="sm" c="blue.7" fw={500} style={{ minWidth: 0 }}>
                    Scaled from {recipe.originalServings} to {recipe.servings} {recipe.servings === 1 ? 'serving' : 'servings'}
                  </Text>
                  {onViewCanonical && (
                    <Group
                      gap="xs"
                      wrap="nowrap"
                      style={{ cursor: 'pointer', flexShrink: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewCanonical();
                      }}
                    >
                      {!isTabletOrSmaller && (
                        <Text
                          size="sm"
                          c="blue"
                          fw={500}
                        >
                          View original
                        </Text>
                      )}
                      <IconExternalLink size={16} color="var(--mantine-color-blue-6)" />
                    </Group>
                  )}
                </Group>
              </Paper>
            )}

          </Box>

          {/* Metadata Section */}
          <Stack gap="md" style={{ flex: 1 }}>
            {/* Description */}
            {isEditing ? (
              <DescriptionEditor initialValue={editedRecipe.description || ''} onDescriptionChange={setCurrentDescription} />
            ) : recipe.description ? (
              <Text size="md" c="dimmed">
                {recipe.description}
              </Text>
            ) : null}

            {/* Recipe Metadata */}
            {isEditing ? (
              <Stack gap="md">
                <Group gap="lg" wrap="wrap">
                  <NumberInput
                    label={
                      <Group gap="xs" align="flex-start">
                        <IconSlice size={20} style={{ marginTop: 2 }} />
                        <Text size="sm" fw={500}>Prep time</Text>
                      </Group> 
                    }
                    variant="unstyled"
                    value={editedRecipe.prepTimeMinutes}
                    onChange={(value) => setEditedRecipe(prev => ({ 
                      ...prev, 
                      prepTimeMinutes: value as number,
                      totalTimeMinutes: (value as number) + prev.cookTimeMinutes
                    }))}
                    min={0}
                    style={{ width: 120 }}
                    styles={{
                      label: {
                        marginBottom: 0,
                      },
                      root: {
                        display: 'flex',
                        flexDirection: 'column',
                      },
                      input: {
                         paddingLeft: '27px',
                      },
                    }}
                    handlersRef={prepTimeHandlersRef}
                    rightSection={
                      <Box
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: rem(2),
                          paddingRight: theme.spacing.xs,
                        }}
                      >
                        <Box style={{ pointerEvents: 'none', marginRight: theme.spacing.xs }}>
                          min
                        </Box>
                        <Stack
                          gap={1}
                          style={{ height: '100%', padding: '2px', marginRight: '2px' }}
                        >
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            style={{ height: '14px', minHeight: '14px' }}
                            onClick={() => prepTimeHandlersRef.current?.increment()}
                          >
                            <IconChevronUp size={12} color="var(--mantine-color-dimmed)" />
                          </ActionIcon>
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            style={{ height: '14px', minHeight: '14px' }}
                            onClick={() => prepTimeHandlersRef.current?.decrement()}
                          >
                            <IconChevronDown size={12} color="var(--mantine-color-dimmed)" />
                          </ActionIcon>
                        </Stack>
                      </Box>
                    } 
                  />
                  <NumberInput
                    label={
                      <Group gap="xs" align="flex-start">
                        <IconFlame size={20} style={{ marginTop: 2 }} />
                        <Text size="sm" fw={500}>Cook time</Text>
                      </Group> 
                    }
                    variant="unstyled"
                    value={editedRecipe.cookTimeMinutes}
                    onChange={(value) => setEditedRecipe(prev => ({ 
                      ...prev, 
                      cookTimeMinutes: value as number,
                      totalTimeMinutes: prev.prepTimeMinutes + (value as number)
                    }))}
                    min={0}
                    style={{ width: 120 }}
                    styles={{
                      label: {
                        marginBottom: 0,
                      },
                      root: {
                        display: 'flex',
                        flexDirection: 'column',
                      },
                      input: {
                         paddingLeft: '27px',
                      },
                    }}
                    handlersRef={cookTimeHandlersRef}
                    rightSection={
                      <Box
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: rem(2),
                          paddingRight: theme.spacing.xs,
                        }}
                      >
                        <Box style={{ pointerEvents: 'none', marginRight: theme.spacing.xs }}>
                          min
                        </Box>
                        <Stack
                          gap={1}
                          style={{ height: '100%', padding: '2px', marginRight: '2px' }}
                        >
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            style={{ height: '14px', minHeight: '14px' }}
                            onClick={() => cookTimeHandlersRef.current?.increment()}
                          >
                            <IconChevronUp size={12} color="var(--mantine-color-dimmed)" />
                          </ActionIcon>
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            style={{ height: '14px', minHeight: '14px' }}
                            onClick={() => cookTimeHandlersRef.current?.decrement()}
                          >
                            <IconChevronDown size={12} color="var(--mantine-color-dimmed)" />
                          </ActionIcon>
                        </Stack>
                      </Box>
                    } 
                  />
                  <NumberInput
                    label={
                      <Group gap="xs" align="flex-start">
                        <IconUsers size={20} style={{ marginTop: 2 }} />
                        <Text size="sm" fw={500}>Servings</Text>
                      </Group> 
                    }
                    variant="unstyled"
                    value={editedRecipe.servings}
                    onChange={(value) => setEditedRecipe(prev => ({ ...prev, servings: value as number }))}
                    min={1}
                    style={{ width: 100 }}
                    styles={{
                      label: {
                        marginBottom: 0,
                      },
                      root: {
                        display: 'flex',
                        flexDirection: 'column',
                      },
                      input: {
                         paddingLeft: '27px',
                      },
                    }}
                    handlersRef={servingsHandlersRef}
                    rightSection={
                      <Box
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: rem(2),
                          paddingRight: theme.spacing.xs,
                        }}
                      >
                        <Stack
                          gap={1}
                          style={{ height: '100%', padding: '2px', marginRight: '2px' }}
                        >
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            style={{ height: '14px', minHeight: '14px' }}
                            onClick={() => servingsHandlersRef.current?.increment()}
                          >
                            <IconChevronUp size={12} color="var(--mantine-color-dimmed)" />
                          </ActionIcon>
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            style={{ height: '14px', minHeight: '14px' }}
                            onClick={() => servingsHandlersRef.current?.decrement()}
                          >
                            <IconChevronDown size={12} color="var(--mantine-color-dimmed)" />
                          </ActionIcon>
                        </Stack>
                      </Box>
                    } 
                  />
                  <Select
                    label={
                      <Group gap="xs" align="flex-start">
                        <IconChefHat size={20} style={{ marginTop: 2 }} />
                        <Text size="sm" fw={500}>Difficulty</Text>
                      </Group> 
                    }
                    variant="unstyled"
                    value={editedRecipe.difficulty}
                    onChange={(value) => setEditedRecipe(prev => ({ ...prev, difficulty: value as Recipe['difficulty'] }))}
                    data={[
                      { value: 'easy', label: 'Easy' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'hard', label: 'Hard' }
                    ]}
                    style={{ width: 120 }}
                    styles={{
                      label: {
                        marginBottom: 0,
                      },
                      root: {
                        display: 'flex',
                        flexDirection: 'column',
                      },
                      input: {
                         paddingLeft: '27px',
                      },
                    }}
                  />
                </Group>
                
                <Group gap="lg" align="flex-start" wrap="wrap">
                  <Box style={{ flex: 1, minWidth: 300, maxWidth: 400 }}>
                    <MultiSelect
                      label="Dietary Restrictions"
                      variant="unstyled"
                      placeholder={editedRecipe.dietaryRestrictions?.length ? undefined : "Select dietary restrictions"}
                      value={editedRecipe.dietaryRestrictions || []}
                      onChange={(value) => setEditedRecipe(prev => ({ ...prev, dietaryRestrictions: value }))}
                      data={[
                        'Vegetarian',
                        'Vegan',
                        'Gluten-Free',
                        'Dairy-Free',
                        'Nut-Free',
                        'Soy-Free',
                        'Egg-Free',
                        'Pescatarian',
                        'Keto',
                        'Paleo',
                        'Low-Calorie',
                        'Low-Carb',
                        'Low-Sodium',
                        'Low-Fat',
                        'Low-Saturated-Fat',
                        'Low-Cholesterol',
                        'Low-Sugar',
                        'Low-Residue',
                        'Low-FODMAP',
                        'High-Protein',
                        'High-Fiber',
                        'Whole30',
                        'Kosher',
                        'Halal',
                      ]}
                      searchable
                      styles={{
                        section: {
                          alignItems: 'flex-start',
                          paddingTop: '7px'
                        }
                      }}
                    />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 300, maxWidth: 400 }}>
                    <MultiSelect
                      label="Tags"
                      variant="unstyled"
                      placeholder={editedRecipe.tags?.length ? undefined : "Add tags"}
                      value={editedRecipe.tags || []}
                      onChange={(value) => setEditedRecipe(prev => ({ ...prev, tags: value }))}
                      data={[
                        { value: 'one-pot', label: 'One-Pot' },
                        { value: 'sheet pan', label: 'Sheet Pan' },
                        { value: 'slow cooker', label: 'Slow Cooker' },
                        { value: 'pressure cooker', label: 'Pressure Cooker' },
                        { value: 'air fryer', label: 'Air Fryer' },
                        { value: 'fry', label: 'Fry' },
                        { value: 'sauté', label: 'Sauté' },
                        { value: 'grill', label: 'Grill' },
                        { value: 'bake', label: 'Bake' },
                        { value: 'roast', label: 'Roast' },
                        { value: 'boil', label: 'Boil' },
                        { value: 'steam', label: 'Steam' },
                        { value: 'sous vide', label: 'Sous Vide' },
                        { value: 'stir-fry', label: 'Stir-Fry' },
                        { value: 'no-cook', label: 'No-Cook' },
                        { value: 'quick', label: 'Quick' },
                        { value: 'healthy', label: 'Healthy' },
                        { value: 'comfort food', label: 'Comfort Food' },
                        { value: 'budget-friendly', label: 'Budget-Friendly' },
                        { value: 'spicy', label: 'Spicy' },
                        { value: 'sweet', label: 'Sweet' },
                        { value: 'savory', label: 'Savory' },
                        { value: 'tangy', label: 'Tangy' },
                        { value: 'smoky', label: 'Smoky' },
                        { value: 'rich', label: 'Rich' },
                        { value: 'fresh', label: 'Fresh' },
                        { value: 'classic', label: 'Classic' },
                        { value: 'kid-friendly', label: 'Kid-Friendly' },
                        { value: 'crowd-pleaser', label: 'Crowd-Pleaser' },
                        { value: 'special occasion', label: 'Special Occasion' },
                      ]}
                      searchable
                      maxValues={8}
                      styles={{
                        section: {
                          alignItems: 'flex-start',
                          paddingTop: '7px'
                        }
                      }}
                    />
                  </Box>
                </Group>
              </Stack>
            ) : (
              <Box className={classes.statGridContainer}>
              <Box className={classes.statGrid}>
                <Group gap="xs" align="flex-start" wrap="nowrap">
                  <IconSlice size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Box>
                    <Text size="sm" fw={500}>Prep time</Text>
                    <Text size="sm" c="dimmed">{formatTime(recipe.prepTimeMinutes)}</Text>
                  </Box>
                </Group>

                <Group gap="xs" align="flex-start" wrap="nowrap">
                  <IconFlame size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Box>
                    <Text size="sm" fw={500}>Cook time</Text>
                    <Text size="sm" c="dimmed">{formatTime(recipe.cookTimeMinutes)}</Text>
                  </Box>
                </Group>

                <Group gap="xs" align="flex-start" wrap="nowrap">
                  <IconClock size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Box>
                    <Text size="sm" fw={500}>Total time</Text>
                    <Text size="sm" c="dimmed">{formatTime(recipe.totalTimeMinutes)}</Text>
                  </Box>
                </Group>

                <Group gap="xs" align="flex-start" wrap="nowrap">
                  <IconUsers size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Box>
                    <Text size="sm" fw={500}>Servings</Text>
                    <Text size="sm" c="dimmed">{recipe.servings}</Text>
                  </Box>
                </Group>

                {recipe.difficulty && (
                  <Group gap="xs" align="flex-start" wrap="nowrap">
                    <IconChefHat size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Text size="sm" fw={500}>Difficulty</Text>
                      <Badge
                        size="sm"
                        variant="light"
                        color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                      >
                        {recipe.difficulty}
                      </Badge>
                    </Box>
                  </Group>
                )}

                {editedRecipe.nutrition && (
                  <Group gap="xs" align="flex-start" wrap="nowrap">
                    <IconChartBar size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Text size="sm" fw={500}>Nutrition</Text>
                      <Text
                        component="span"
                        size="sm"
                        c="dimmed"
                        style={{ cursor: 'pointer', width: 'fit-content', display: 'inline-block' }}
                        onClick={() => setNutritionDrawerOpened(true)}
                      >
                        <Text
                          component="span"
                          inherit
                          style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '2px' }}
                        >
                          {editedRecipe.nutrition.perServing.calories} cal/serving
                        </Text>
                      </Text>
                    </Box>
                  </Group>
                )}

                {editedRecipe.costEstimate && (
                  <Group gap="xs" align="flex-start" wrap="nowrap">
                    <IconCoin size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Text size="sm" fw={500}>Cost</Text>
                      <CostPerServingPopover
                        costEstimate={editedRecipe.costEstimate}
                        dineOut={recipe.estimatedDineOutPricePerServing}
                        dimmed
                      />
                    </Box>
                  </Group>
                )}

                {/* Source is always populated on the details page (unlike
                    cards, where catalog recipes are the unmarked default):
                    a labeled metadata field should not sit empty. Exactly one
                    badge — authorship only (Me / USDA / Meals You Love).
                    Endorsement lives on the hero ribbon, never here. */}
                <Group gap="xs" align="flex-start" wrap="nowrap">
                  {recipe.origin === 'user_recipe' ? (
                    <IconUser size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                  ) : recipe.source ? (
                    <IconRosetteDiscountCheck size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                  ) : (
                    <IconBook2 size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                  )}
                  <Box>
                    <Text size="sm" fw={500}>Source</Text>
                    {recipe.origin === 'user_recipe' ? (
                      <Badge size="sm" variant="light" color="teal">
                        {isOwner ? 'Me' : 'Member recipe'}
                      </Badge>
                    ) : recipe.source ? (
                      <Tooltip label={recipeSourceTooltip(recipe.source)} {...sourceTooltipProps}>
                        <Badge size="sm" variant="light" color="indigo" style={sourceBadgeStyle}>
                          {recipeSourceShortLabel(recipe.source)}
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Image
                        src="/horizontal_lockup_small.png"
                        alt="Meals You Love"
                        h={18}
                        w="auto"
                        fit="contain"
                        mt={4}
                        style={{ display: 'block' }}
                      />
                    )}
                  </Box>
                </Group>
              </Box>
              </Box>
            )}

            {/* Action Buttons - Desktop (in metadata section) */}
                {recipe.id !== 'new' && !isEditing && (
                  <Group gap="sm" display={{ base: 'none', md: 'flex' }} style={{ width: '100%' }}>
                    <Button
                      variant="light"
                      color="blue"
                      size="sm"
                      leftSection={<IconToolsKitchen size={18} />}
                      onClick={() => {
                        navigate(`${buildRecipePath(recipe, ownerId || user?.uid || '')}/cook`, {
                            state: {
                              // A pending summary has no steps: let cook mode fetch
                              recipe: contentPending ? undefined : recipe,
                              ...(mealPlanId && assignmentId && {
                                mealPlanId,
                                assignmentId
                              })
                            }
                          });
                      }}
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        borderRadius: '28px'
                      }}
                    >
                      Cook mode
                    </Button>
                    
                    <Button
                      onClick={handleAddToInstacart}
                      variant="filled"
                      size="sm"
                      loading={addingToInstacart}
                      disabled={addingToInstacart}
                      className={classes.instacartButton}
                      style={{
                        backgroundColor: addingToInstacart ? '#e9ecef' : '#003D29',
                        color: addingToInstacart ? '#6c757d' : '#FAF1E5',
                        flex: 1,
                        minWidth: '120px',
                        borderRadius: '28px'
                      }}
                    >
                      <Group gap={8} align="center">
                        <img
                          src="/images/instacart_carrot.png"
                          alt="Instacart"
                          style={{
                            height: '18px',
                            width: 'auto',
                            filter: addingToInstacart ? 'grayscale(1) brightness(0.6)' : 'none'
                          }}
                        />
                        <span style={{ fontWeight: 600 }}>Get recipe ingredients</span>
                      </Group>
                    </Button>
                  </Group>
                )}
          </Stack>
        </Flex>

        {/* Recipe Information */}
        {!isEditing && (
          <Stack gap="md">
            {/* Usage Count, Dietary Restrictions, and Tags in View Mode */}
            <Stack gap="md">
              {(isOwner && recipe.usageCount && recipe.usageCount > 0) || (recipe.dietaryRestrictions && recipe.dietaryRestrictions.length > 0) ? (
                <Group gap="xs" wrap="wrap">
                  {isOwner && recipe.usageCount && recipe.usageCount > 0 ? (
                    <Badge size="md" variant="light" color="blue">
                      Used {recipe.usageCount} time{recipe.usageCount !== 1 ? 's' : ''}
                    </Badge>
                  ) : null}
                  {(() => {
                    // Show skeleton badges while user preferences are loading
                    if (userDietaryRestrictions === null && recipe.dietaryRestrictions && recipe.dietaryRestrictions.length > 0) {
                      const maxVisible = 3;
                      const restrictionCount = recipe.dietaryRestrictions.length;
                      const visibleCount = Math.min(restrictionCount, maxVisible);
                      const hasMore = restrictionCount > maxVisible;
                      // Vary widths to approximate actual badge sizes
                      const widths = [90, 100, 85];

                      return (
                        <>
                          {Array.from({ length: visibleCount }).map((_, i) => (
                            <Skeleton key={`skeleton-${i}`} height={20} width={widths[i] || 90} radius="xl" />
                          ))}
                          {hasMore && (
                            <Skeleton height={20} width={70} radius="xl" />
                          )}
                        </>
                      );
                    }

                    // Don't render anything if no dietary restrictions or still loading with none
                    if (userDietaryRestrictions === null) {
                      return null;
                    }

                    const { visible, remaining } = getPrioritizedDietaryRestrictions(recipe.dietaryRestrictions);
                    return (
                      <>
                        {visible.map((restriction) => (
                          <Badge key={restriction} size="md" variant="light" color="green">
                            {restriction}
                          </Badge>
                        ))}
                        <MoreRestrictionsMenu restrictions={remaining} />
                      </>
                    );
                  })()}
                </Group>
              ) : null}
              {recipe.tags && recipe.tags.length > 0 ? (
                <Group gap="xs" wrap="wrap">
                  {filterTagsForViewer(recipe.tags, userHouseholdSegment).map((tag) => (
                    <Badge key={tag} size="md" variant="outline" color="gray">
                      {tag}
                    </Badge>
                  ))}
                </Group>
              ) : null}

              {/* Action Buttons - Mobile/Tablet (after tags) */}
              {recipe.id !== 'new' && !isEditing && (
                <>
                  {/* Stacked for mobile only */}
                  <Stack gap="sm" mt="md" display={{ base: 'flex', sm: 'none' }}>
                    <Button
                      variant="light"
                      color="blue"
                      size="md"
                      leftSection={<IconToolsKitchen size={18} />}
                      onClick={() => {
                        navigate(`${buildRecipePath(recipe, ownerId || user?.uid || '')}/cook`, {
                            state: {
                              // A pending summary has no steps: let cook mode fetch
                              recipe: contentPending ? undefined : recipe,
                              ...(mealPlanId && assignmentId && {
                                mealPlanId,
                                assignmentId
                              })
                            }
                          });
                      }}
                      fullWidth
                      style={{ borderRadius: '28px' }}
                    >
                      Cook mode
                    </Button>
                    
                    <Button
                      onClick={handleAddToInstacart}
                      variant="filled"
                      size="md"
                      loading={addingToInstacart}
                      disabled={addingToInstacart}
                      fullWidth
                      className={classes.instacartButton}
                      style={{
                        backgroundColor: addingToInstacart ? '#e9ecef' : '#003D29',
                        color: addingToInstacart ? '#6c757d' : '#FAF1E5',
                        borderRadius: '28px'
                      }}
                    >
                      <Group gap={8} align="center">
                        <img 
                          src="/images/instacart_carrot.png" 
                          alt="Instacart" 
                          style={{ 
                            height: '18px', 
                            width: 'auto',
                            filter: addingToInstacart ? 'grayscale(1) brightness(0.6)' : 'none'
                          }}
                        />
                        <span style={{ fontWeight: 600 }}>Get recipe ingredients</span>
                      </Group>
                    </Button>
                  </Stack>
                  
                  {/* Side by side for tablet only */}
                  <Group gap="sm" mt="md" display={{ base: 'none', sm: 'flex', md: 'none' }}>
                    <Button
                      variant="light"
                      color="blue"
                      size="md"
                      leftSection={<IconToolsKitchen size={18} />}
                      onClick={() => {
                        navigate(`${buildRecipePath(recipe, ownerId || user?.uid || '')}/cook`, {
                            state: {
                              // A pending summary has no steps: let cook mode fetch
                              recipe: contentPending ? undefined : recipe,
                              ...(mealPlanId && assignmentId && {
                                mealPlanId,
                                assignmentId
                              })
                            }
                          });
                      }}
                      style={{ flex: 1, borderRadius: '28px' }}
                    >
                      Cook mode
                    </Button>
                    
                    <Button
                      onClick={handleAddToInstacart}
                      variant="filled"
                      size="md"
                      loading={addingToInstacart}
                      disabled={addingToInstacart}
                      className={classes.instacartButton}
                      style={{
                        backgroundColor: addingToInstacart ? '#e9ecef' : '#003D29',
                        color: addingToInstacart ? '#6c757d' : '#FAF1E5',
                        flex: 1,
                        borderRadius: '28px'
                      }}
                    >
                      <Group gap={8} align="center">
                        <img 
                          src="/images/instacart_carrot.png" 
                          alt="Instacart" 
                          style={{ 
                            height: '18px', 
                            width: 'auto',
                            filter: addingToInstacart ? 'grayscale(1) brightness(0.6)' : 'none'
                          }}
                        />
                        <span style={{ fontWeight: 600 }}>Get recipe ingredients</span>
                      </Group>
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          </Stack>
        )}

        <Grid>
          <Grid.Col span={{ base: 12, md: 5 }} style={{ minWidth: 0 }}>
            <Stack gap="lg" style={{ height: '100%' }}>
              {/* Ingredients — sticky on desktop in view mode */}
              <Box style={!isEditing ? {
                position: 'sticky',
                top: 76,
                alignSelf: 'flex-start',
                width: '100%',
              } : undefined}>
              <Stack gap="md">
                <Title order={4} ref={ingredientsTitleRef}>Ingredients</Title>

                {isEditing ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleIngredientDragStart}
                    onDragEnd={handleIngredientDragEnd}
                  >
                    <SortableContext
                      items={editedRecipe.ingredients?.map((_, i) => getIngredientId(i)) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack gap="md">
                        {editedRecipe.ingredients?.map((ingredient, index) => (
                          <SortableIngredient
                            key={getIngredientId(index)}
                            id={getIngredientId(index)}
                          >
                            <IngredientEditor
                              index={index}
                              ingredient={ingredient}
                              onIngredientChange={handleIngredientChange}
                              onRemove={handleIngredientRemove}
                              onIngredientNameChange={handleIngredientNameChange}
                            />
                          </SortableIngredient>
                        ))}
                      </Stack>
                    </SortableContext>
                    <DragOverlay>
                      {activeIngredientId ? (
                        (() => {
                          // Find the index by looking up the stable ID
                          let index = -1;
                          for (let i = 0; i < (editedRecipe.ingredients?.length || 0); i++) {
                            if (getIngredientId(i) === activeIngredientId) {
                              index = i;
                              break;
                            }
                          }
                          const ingredient = editedRecipe.ingredients?.[index];
                          if (!ingredient || index === -1) return null;

                          return (
                            <Box style={{ cursor: 'grabbing' }}>
                              <IngredientEditor
                                index={index}
                                ingredient={ingredient}
                                onIngredientChange={handleIngredientChange}
                                onRemove={handleIngredientRemove}
                                onIngredientNameChange={handleIngredientNameChange}
                              />
                            </Box>
                          );
                        })()
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                ) : (
                  <Paper p={{ base: "lg", sm: "md" }} radius="lg" withBorder>
                    <Stack gap={0}>
                      {contentPending && <IngredientRowsSkeleton />}
                      {recipe.ingredients?.map((ingredient, index) => (
                        editingIngredientIndex === index ? (
                          // Inline editing mode
                          <Box key={index} py="xs" style={{
                            borderBottom: index < recipe.ingredients.length - 1 ? '1px solid var(--mantine-color-gray-2)' : 'none',
                          }}>
                            <Stack gap="sm">
                              <IngredientEditor
                                index={index}
                                ingredient={ingredient}
                                onIngredientChange={handleInlineIngredientChange}
                                onRemove={handleRemoveInlineIngredient}
                                noBorder={true}
                              />
                              <Group gap="sm">
                                <Button
                                  size="sm"
                                  onClick={handleSaveInlineEdit}
                                  loading={savingInlineEdit}
                                  leftSection={<IconCheck size={16} />}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="light"
                                  color="gray"
                                  onClick={handleCancelInlineEdit}
                                  disabled={savingInlineEdit}
                                  leftSection={<IconX size={16} />}
                                >
                                  Cancel
                                </Button>
                              </Group>
                            </Stack>
                          </Box>
                        ) : (
                          // View mode
                          <Box key={index} py="xs"
                            onClick={(e) => {
                              // Don't lock if click was on interactive elements (menu, pantry badge, etc.)
                              if ((e.target as HTMLElement).closest('button, [role="menuitem"], .mantine-Menu-dropdown, .mantine-ActionIcon-root, a')) return;
                              e.stopPropagation();
                              const stepIndices = new Set<number>();
                              recipe.instructions?.forEach((inst, i) => {
                                if (inst.ingredients?.includes(ingredient.name)) {
                                  stepIndices.add(i);
                                }
                              });
                              if (stepIndices.size === 0) return;
                              // Clear step→ingredient highlights (mutually exclusive)
                              setHighlightedIngredients(new Set());
                              setLockedStepIndex(null);
                      
                              if (lockedIngredientName === ingredient.name) {
                                // Unlock
                                setLockedIngredientName(null);
                                setActiveIngredientHighlight(null);
                                setHighlightedSteps(new Set());
                              } else {
                                // Lock to this ingredient
                                setLockedIngredientName(ingredient.name);
                                setActiveIngredientHighlight(ingredient.name);
                                setHighlightedSteps(stepIndices);
                              }
                            }}
                            style={{
                            cursor: recipe.instructions?.some(inst => inst.ingredients?.includes(ingredient.name)) ? 'pointer' : undefined,
                            borderBottom: index < recipe.ingredients.length - 1
                              ? highlightedIngredients.size > 0 && highlightedIngredients.has(ingredient.name) && !highlightedIngredients.has(recipe.ingredients[index + 1]?.name)
                                ? '1px solid color-mix(in srgb, var(--mantine-color-gray-2) 30%, transparent)'
                                : '1px solid var(--mantine-color-gray-2)'
                              : 'none',
                            display: 'flex',
                            gap: 'var(--mantine-spacing-xs)',
                            alignItems: 'flex-start',
                            transition: 'opacity 200ms ease',
                            ...(highlightedIngredients.size > 0 && !highlightedIngredients.has(ingredient.name) ? {
                              opacity: 0.3,
                            } : {}),
                          }} className={classes.ingredientItem}>
                            {/* Content column: name text, plus (mobile only)
                                the badges on their own line below — inline
                                badges would crush the text to a sliver on
                                narrow screens, and letting them wrap would
                                knock the kebab menu out of its column */}
                            <Box style={{ flex: '1 1 0', minWidth: 0 }}>
                              {/* Optional ingredients that clash with a diet the
                                  household holds are faded: the recipe still
                                  lists them, but this is the line they skip,
                                  and it is why the grocery list leaves it out. */}
                              <Text fw={activeIngredientHighlight === ingredient.name || (highlightedIngredients.size > 0 && highlightedIngredients.has(ingredient.name)) ? 600 : undefined} style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                opacity: ingredient.conflictsWith?.length ? 0.45 : undefined,
                              }}>
                                <Text component="span" fw={activeIngredientHighlight === ingredient.name || (highlightedIngredients.size > 0 && highlightedIngredients.has(ingredient.name)) ? 700 : 500}>
                                  {formatIngredientQuantity(ingredient)}
                                </Text>
                                {' '}
                                {formatIngredientName(ingredient)}
                                {formatIngredientPreparation(ingredient) && (
                                  <Text component="span" c="dimmed">
                                    {' '}— {formatIngredientPreparation(ingredient)}
                                  </Text>
                                )}
                              </Text>
                              {(ingredient.optional || (user && !pantryLoading && isItemInPantry(ingredient.name, pantryItems))) && (
                                <Group gap="xs" mt={4} hiddenFrom="sm">
                                  {user && !pantryLoading && isItemInPantry(ingredient.name, pantryItems) && (
                                    <Badge
                                      size="xs"
                                      variant="light"
                                      color="green"
                                      style={{ cursor: 'pointer' }}
                                      leftSection={<IconCheck size={10} />}
                                      onClick={(e) => { e.stopPropagation(); navigate(`/pantry?search=${encodeURIComponent(ingredient.name)}`); }}
                                    >
                                      In Pantry
                                    </Badge>
                                  )}
                                  {ingredient.optional && (
                                    <Badge size="xs" variant="light" color="gray" style={{ cursor: 'inherit' }}>
                                      optional
                                    </Badge>
                                  )}
                                  {ingredient.conflictsWith?.length ? (
                                    <Badge size="xs" variant="light" color="orange" style={{ cursor: 'inherit' }}>
                                      not {ingredient.conflictsWith.join(", ").toLowerCase()}
                                    </Badge>
                                  ) : null}
                                </Group>
                              )}
                            </Box>
                            <Group gap="xs" style={{ flexShrink: 0 }}>
                              {user && !pantryLoading && isItemInPantry(ingredient.name, pantryItems) && (
                                <Badge
                                  size="xs"
                                  variant="light"
                                  color="green"
                                  style={{ flexShrink: 0, cursor: 'pointer' }}
                                  leftSection={<IconCheck size={10} />}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/pantry?search=${encodeURIComponent(ingredient.name)}`); }}
                                  visibleFrom="sm"
                                >
                                  In Pantry
                                </Badge>
                              )}
                              {ingredient.optional && (
                                <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0, cursor: 'inherit' }} visibleFrom="sm">
                                  optional
                                </Badge>
                              )}
                              {ingredient.conflictsWith?.length ? (
                                <Badge size="xs" variant="light" color="orange" style={{ flexShrink: 0, cursor: 'inherit' }} visibleFrom="sm">
                                  not {ingredient.conflictsWith.join(", ").toLowerCase()}
                                </Badge>
                              ) : null}
                              {user && (
                                <Menu>
                                  <Menu.Target>
                                    <ActionIcon
                                      size="md"
                                      variant="subtle"
                                      color="gray"
                                      className={classes.menuButton}
                                      loading={addingToPantryIngredient === ingredient.name}
                                    >
                                      <IconDotsVertical size={16} />
                                    </ActionIcon>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    {isOwner && !isScaledRecipe && (
                                    <Menu.Item
                                      leftSection={<IconEdit size={isMobile ? 18 : 14} stroke={1.5} />}
                                      onClick={() => handleStartInlineEdit(index, ingredient)}
                                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                    >
                                      Edit ingredient
                                    </Menu.Item>
                                    )}
                                    <Menu.Item
                                      leftSection={<IconReplace size={isMobile ? 18 : 14} stroke={1.5} />}
                                      onClick={() => handleGetSubstitutions(ingredient)}
                                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                    >
                                      Find substitutes
                                    </Menu.Item>
                                  {!isIngredientInFoodsToAvoid(ingredient.name) && (
                                    <Menu.Item
                                      leftSection={<IconBan size={isMobile ? 18 : 14} stroke={1.5} />}
                                      onClick={() => handleAddToFoodsToAvoid(ingredient)}
                                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                      color="red"
                                    >
                                      Add to foods to avoid
                                    </Menu.Item>
                                  )}
                                  {!isItemInPantry(ingredient.name, pantryItems) && (
                                    <>
                                      <Menu.Divider />
                                      <Menu.Label>Add to my pantry</Menu.Label>
                                      <Menu.Item
                                        leftSection={<IconSalt size={isMobile ? 18 : 14} stroke={1.5} />}
                                        onClick={() => handleAddAsStaple(ingredient)}
                                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                      >
                                        Add as staple
                                      </Menu.Item>
                                      <Menu.Item
                                        leftSection={<IconScale size={isMobile ? 18 : 14} stroke={1.5} />}
                                        onClick={() => handleAddAsIngredient(ingredient)}
                                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                      >
                                        Add with quantity
                                      </Menu.Item>
                                    </>
                                  )}
                                </Menu.Dropdown>
                              </Menu>
                            )}
                          </Group>
                        </Box>
                        )
                      ))}
                    </Stack>
                  </Paper>
                )}
                
                {isEditing && (
                  <Button
                    leftSection={<IconPlus size={16} />}
                    variant="light"
                    size="sm"
                    onClick={() => {
                      const newIngredient = {
                        name: '',
                        quantity: 1,
                        unit: '',
                        preparation: '',
                        optional: false,
                        productSearchTerms: []
                      };
                      const newIngredients = [...ingredientsRef.current, newIngredient];
                      ingredientsRef.current = newIngredients;
                      
                      // Update available ingredient names for instruction editors  
                      updateAvailableIngredientNames();
                      
                      setEditedRecipe(prev => ({ ...prev, ingredients: newIngredients }));
                      
                      // Focus the new ingredient name input after state update
                      setTimeout(() => {
                        const newIndex = newIngredients.length - 1;
                        if (ingredientRefs.current && ingredientRefs.current[newIndex]) {
                          ingredientRefs.current[newIndex].focus();
                        }
                      }, 100);
                    }}
                  >
                    Add ingredient
                  </Button>
                )}
              </Stack>

              {/* Notes (Wide Screen) */}
              <Box display={{ base: 'none', md: 'block' }} mt="lg">
                {isEditing ? (
                  <NotesEditor initialValue={editedRecipe.notes || ''} onNotesChange={setCurrentNotes} />
                ) : contentPending ? (
                  <NotesSkeleton />
                ) : recipe.notes && (
                  <Stack gap="md">
                    <Title order={4}>Notes</Title>
                    <Paper p={{ base: 'lg', sm: 'md' }} radius="lg" withBorder>
                      <Text style={{ whiteSpace: 'pre-wrap' }}>{recipe.notes}</Text>
                    </Paper>
                  </Stack>
                )}
              </Box>
              </Box>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 7 }}>
            {/* Instructions */}
            <Stack gap="md">
              <Title order={4}>Instructions</Title>

              {/* Naive-scaled recipes keep their original instruction text, so
                  amounts mentioned in the steps may not match the scaled
                  ingredient list. Shown only when the steps actually mention
                  quantities (backend sets the flag). */}
              {!isEditing && isScaledRecipe && recipe.hasUnscaledStepQuantities && (
                <Alert color="blue" radius="lg" icon={<IconAlertCircle size={18} />} py="xs">
                  Amounts in these steps may reflect the original {recipe.originalServings}-serving
                  version — follow the ingredient list, which is scaled to {recipe.servings}.
                </Alert>
              )}

              <Stack gap="sm">
                {isEditing ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleInstructionDragStart}
                    onDragEnd={handleInstructionDragEnd}
                  >
                    <SortableContext
                      items={editedRecipe.instructions?.map((_, i) => getInstructionId(i)) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack gap="sm">
                        {editedRecipe.instructions?.map((instruction, index) => {
                        const recipeInstruction = instruction as RecipeInstruction;

                        return (
                          <SortableInstructionEditor
                            key={getInstructionId(index)}
                            id={getInstructionId(index)}
                            index={index}
                            instruction={recipeInstruction}
                            availableIngredients={availableIngredientNames}
                            instructionRef={instructionRefs}
                            onTextChange={handleInstructionTextChange}
                            onIngredientsChange={handleInstructionIngredientsChange}
                            onRemove={handleInstructionRemove}
                          />
                        );
                      })}
                      </Stack>
                    </SortableContext>
                    <DragOverlay>
                      {activeInstructionId ? (
                        (() => {
                          // Find the index by looking up the stable ID
                          let index = -1;
                          for (let i = 0; i < (editedRecipe.instructions?.length || 0); i++) {
                            if (getInstructionId(i) === activeInstructionId) {
                              index = i;
                              break;
                            }
                          }
                          const instruction = editedRecipe.instructions?.[index] as RecipeInstruction;
                          if (!instruction || index === -1) return null;

                          return (
                            <Paper p="md" radius="lg" withBorder style={{ cursor: 'grabbing', position: 'relative' }}>
                              <ActionIcon
                                color="orange"
                                variant="subtle"
                                style={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  cursor: 'grabbing',
                                }}
                              >
                                <IconX size={16} />
                              </ActionIcon>
                              <Stack gap="md">
                                <Group gap="md" align="flex-start">
                                  <Badge
                                    size="xl"
                                    variant="filled"
                                    color="blue"
                                    circle
                                    style={{ cursor: 'grabbing' }}
                                  >
                                    {index + 1}
                                  </Badge>
                                  <Box style={{ flex: 1, paddingRight: 32, minWidth: 0 }}>
                                    <Textarea
                                      value={instruction.text}
                                      variant="unstyled"
                                      readOnly
                                      rows={3}
                                      styles={{
                                        input: {
                                          cursor: 'grabbing',
                                          wordBreak: 'break-word',
                                          overflowWrap: 'break-word',
                                        }
                                      }}
                                    />
                                    {availableIngredientNames.length > 0 && (
                                      <Box mt="sm">
                                        <MultiSelect
                                          label="Ingredients used in this step"
                                          variant="unstyled"
                                          placeholder={instruction.ingredients?.length ? undefined : "Select ingredients relevant to this step..."}
                                          value={instruction.ingredients || []}
                                          onChange={() => {}}
                                          data={Array.from(new Set(availableIngredientNames)).map(name => ({ value: name, label: name }))}
                                          searchable
                                          size="sm"
                                          styles={{
                                            section: {
                                              alignItems: 'flex-start',
                                              paddingTop: '7px'
                                            }
                                          }}
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </Group>
                              </Stack>
                            </Paper>
                          );
                        })()
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                ) : contentPending ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <Paper key={`pending-${i}`} p={{ base: "lg", sm: "md" }} radius="lg" withBorder>
                      <Group gap="md" align="flex-start" wrap="nowrap">
                        <Skeleton height={32} width={32} radius="xl" style={{ flexShrink: 0 }} />
                        <Stack gap="xs" mt={4} style={{ flex: 1 }}>
                          <Skeleton height={14} width="100%" />
                          <Skeleton height={14} width={`${60 + ((i * 13) % 30)}%`} />
                        </Stack>
                      </Group>
                    </Paper>
                  ))
                ) : (
                  recipe.instructions?.map((instruction, index) => {
                    const instructionText = instruction.text;
                    const instructionIngredients = instruction.ingredients || [];

                    return (
                      <Paper
                        key={index}
                        id={`recipe-step-${index}`}
                        p={{ base: "lg", sm: "md" }}
                        radius="lg"
                        withBorder
                        onClick={(e) => {
                          e.stopPropagation();
                          if (instructionIngredients.length === 0) return;
                          // Clear ingredient→step highlights (mutually exclusive)
                          setHighlightedSteps(new Set());
                          setLockedIngredientName(null);
                          setActiveIngredientHighlight(null);
                          if (lockedStepIndex === index) {
                            // Unlock
                            setLockedStepIndex(null);
                            setHighlightedIngredients(new Set());
                    
                          } else {
                            // Lock to this step
                            setLockedStepIndex(index);
                            setHighlightedIngredients(new Set(instructionIngredients));
                          }
                        }}
                        style={{
                          transition: 'box-shadow 200ms ease, border-color 200ms ease, opacity 200ms ease',
                          cursor: instructionIngredients.length > 0 ? 'pointer' : undefined,
                          ...((lockedStepIndex === index || highlightedSteps.has(index)) ? {
                              borderColor: 'var(--mantine-color-blue-3)',
                              boxShadow: '0 0 0 1px var(--mantine-color-blue-2)',
                            } : {}),
                          ...(highlightedSteps.size > 0 && !highlightedSteps.has(index) ? {
                              opacity: 0.3,
                            } : {}),
                          ...(flashStepIndex === index ? {
                              borderColor: 'var(--mantine-color-teal-5)',
                              boxShadow: '0 0 0 3px var(--mantine-color-teal-2)',
                            } : {}),
                        }}
                      >
                        <Stack gap="sm">
                          <Group gap="md" align="flex-start">
                            <Badge size="xl" variant="filled" color="blue" circle style={instructionIngredients.length > 0 ? { cursor: 'pointer' } : undefined}>
                              {index + 1}
                            </Badge>
                            <Text style={{ flex: 1, cursor: instructionIngredients.length > 0 ? 'pointer' : undefined }}>
                              <StepTimerText
                                text={instructionText}
                                timerKey={`${recipe.id}:${index}`}
                                stepNumber={index + 1}
                                recipeName={recipe.name}
                                origin={makeTimerOrigin(location, recipe.id, index)}
                              />
                            </Text>
                          </Group>
                          {instructionIngredients && instructionIngredients.length > 0 && (
                            <Group gap="xs" pl={48}>
                              {instructionIngredients.map((ingredientName, idx) => (
                                <Badge
                                  key={idx}
                                  size="sm"
                                  variant="light"
                                  color="blue"
                                  style={{
                                    cursor: 'pointer',
                                    transition: 'opacity 200ms ease',
                                    ...(activeIngredientHighlight && activeIngredientHighlight !== ingredientName ? { opacity: 0.4 } : {}),
                                  }}
                                >
                                  {ingredientName}
                                </Badge>
                              ))}
                            </Group>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })
                )}
              </Stack>
              
              {isEditing && (
                <Button
                  leftSection={<IconPlus size={16} />}
                  variant="light"
                  size="sm"
                  onClick={() => {
                    const newInstruction: RecipeInstruction = { text: '', ingredients: [] };
                    const newInstructions = [...instructionsRef.current, newInstruction];
                    instructionsRef.current = newInstructions;
                    setEditedRecipe(prev => ({ ...prev, instructions: newInstructions }));
                    
                    // Focus the new instruction textarea after state update
                    setTimeout(() => {
                      const newIndex = newInstructions.length - 1;
                      if (instructionRefs.current && instructionRefs.current[newIndex]) {
                        instructionRefs.current[newIndex].focus();
                      }
                    }, 100);
                  }}
                >
                  Add Step
                </Button>
              )}
            </Stack>
          </Grid.Col>

          {/* Notes (Narrow Screen) */}
          <Grid.Col span={12} display={{ base: 'block', md: 'none' }}>
            {isEditing ? (
              <NotesEditor initialValue={editedRecipe.notes || ''} onNotesChange={setCurrentNotes} />
            ) : contentPending ? (
              <NotesSkeleton />
            ) : recipe.notes && (
              <Stack gap="md">
                <Title order={4}>Notes</Title>
                <Paper p={{ base: 'lg', sm: 'md' }} radius="lg" withBorder>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{recipe.notes}</Text>
                </Paper>
              </Stack>
            )}
          </Grid.Col>
        </Grid>

        {/* External-source attribution (e.g. USDA) — end-of-page credits,
            deliberately out of the prime real estate under the description */}
        {!isEditing && <RecipeSourceAttribution recipe={recipe} />}

        {/* Bottom padding for mobile */}
        <Box h="2rem" />
      </Stack>

      {/* Image Modal */}
      <Modal
        opened={imageModalOpened}
        onClose={() => {
          setImageModalOpened(false);
          setSignedImageUrls([]);
          setSelectedImageIndex(0);
        }}
        title={<Text fw={600} size="lg">{`Source Image${signedImageUrls.length > 1 ? 's' : ''} - ${recipe.name}`}</Text>}
        size="xl"
        centered
        radius="lg"
      >
        <Stack gap="md">
          {signedImageUrls.length > 0 && (
            <>
              {/* Main Image Display */}
              <Box
                style={{
                  position: 'relative',
                  borderRadius: 'var(--mantine-radius-md)',
                  overflow: 'hidden',
                  backgroundColor: 'var(--mantine-color-gray-1)',
                  minHeight: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Image
                  src={signedImageUrls[selectedImageIndex]}
                  alt={`Source image ${selectedImageIndex + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain'
                  }}
                  fallbackSrc={undefined}
                />
              </Box>

              {/* Thumbnail Navigation (if multiple images) */}
              {signedImageUrls.length > 1 && (
                <>
                  <Text size="sm" c="dimmed" ta="center">
                    {selectedImageIndex + 1} of {signedImageUrls.length}
                  </Text>
                  <Group gap="sm" justify="center">
                    {signedImageUrls.map((imageUrl, index) => (
                      <Box
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        style={{
                          cursor: 'pointer',
                          borderRadius: 'var(--mantine-radius-sm)',
                          overflow: 'hidden',
                          border: selectedImageIndex === index ? '2px solid var(--mantine-color-blue-5)' : '2px solid transparent',
                          transition: 'border-color 0.2s ease'
                        }}
                        w={60}
                        h={60}
                      >
                        <Image
                          src={imageUrl}
                          alt={`Thumbnail ${index + 1}`}
                          width="100%"
                          height="100%"
                          fit="cover"
                        />
                      </Box>
                    ))}
                  </Group>
                </>
              )}
            </>
          )}
        </Stack>
      </Modal>

      {/* Recipe Rating Modal */}
      <RecipeRatingModal
        opened={ratingModalOpen}
        onClose={() => setRatingModalOpen(false)}
        recipe={recipe}
        userDietaryRestrictions={userDietaryRestrictions || undefined}
        onSubmit={handleRateDownSubmit}
      />

      {/* Substitution Drawer */}
      <SubstitutionDrawer
        opened={substitutionDrawerOpened}
        onClose={() => {
          setSubstitutionDrawerOpened(false);
          setSubstitutionTarget(null);
          setSubstitutionError(null);
        }}
        target={substitutionTarget}
        loading={substitutionLoading}
        error={substitutionError}
      />

      {/* Nutrition Drawer — not for a pending summary, whose nutrition lacks
          the breakdown the drawer would otherwise regenerate */}
      {recipe.nutrition && !contentPending && (
        <NutritionDrawer
          opened={nutritionDrawerOpened}
          onClose={() => setNutritionDrawerOpened(false)}
          nutrition={recipe.nutrition}
          recipeName={recipe.name}
          recipeId={recipe.id}
          isOwner={isOwner}
          ingredientCount={recipe.ingredients?.length}
          isCatalogView={isCatalogView}
        />
      )}

      {/* Pantry Drawer */}
      <PantryItemDrawer
        opened={pantryDrawerOpened}
        onClose={() => {
          setPantryDrawerOpened(false);
          setSelectedIngredientForPantry(null);
        }}
        onSave={handlePantryDrawerSave}
        onDuplicateFound={handlePantryDrawerDuplicateFound}
        prefillName={selectedIngredientForPantry?.name}
        prefillQuantity={selectedIngredientForPantry?.quantity}
        prefillUnit={selectedIngredientForPantry?.unit}
        prefillType="ingredient"
      />

      {/* Mobile Ingredients FAB */}
      {!isEditing && (
        <Box
          hiddenFrom="md"
          style={{
            position: 'fixed',
            bottom: floatingControlsBottom,
            right: 24,
            zIndex: 99,
            opacity: showIngredientsFab ? 1 : 0,
            pointerEvents: showIngredientsFab ? 'auto' : 'none',
            transition: 'bottom 200ms ease, opacity 200ms ease',
          }}
        >
          <Tooltip label="View ingredients" position="left">
            <ActionIcon
              size="xl"
              radius="xl"
              variant="white"
              color="orange"
              onClick={(e) => { e.stopPropagation(); setIngredientDrawerOpened(true); }}
              style={{
                width: 60,
                height: 60,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <IconList size={28} />
            </ActionIcon>
          </Tooltip>
        </Box>
      )}

      {/* Mobile Ingredients Drawer */}
      <ResponsiveDrawer
        opened={ingredientDrawerOpened}
        onClose={() => setIngredientDrawerOpened(false)}
        overlayProps={{ onClick: (e: React.MouseEvent) => e.stopPropagation() }}
        forceBottomSheet
        size="80%"
        title={
          <DrawerHeader
            icon={<IconList size={18} />}
            title="Ingredients"
          />
        }
      >
          <Stack gap={0}>
            {recipe.ingredients?.map((ingredient, index) => (
              <Box key={index} py="xs" style={{
                borderBottom: index < recipe.ingredients.length - 1
                  ? (() => {
                      const isHighlighted = (highlightedIngredients.size > 0 && highlightedIngredients.has(ingredient.name)) || activeIngredientHighlight === ingredient.name;
                      const nextIngredient = recipe.ingredients[index + 1];
                      const isNextHighlighted = nextIngredient && ((highlightedIngredients.size > 0 && highlightedIngredients.has(nextIngredient.name)) || activeIngredientHighlight === nextIngredient.name);
                      const anyHighlightActive = highlightedIngredients.size > 0 || !!activeIngredientHighlight;
                      return anyHighlightActive && isHighlighted && !isNextHighlighted
                        ? '1px solid color-mix(in srgb, var(--mantine-color-gray-2) 30%, transparent)'
                        : '1px solid var(--mantine-color-gray-2)';
                    })()
                  : 'none',
                transition: 'opacity 200ms ease',
                ...((highlightedIngredients.size > 0 && !highlightedIngredients.has(ingredient.name)) || (activeIngredientHighlight && activeIngredientHighlight !== ingredient.name) ? {
                  opacity: 0.3,
                } : {}),
              }}>
                <Text fw={(highlightedIngredients.size > 0 && highlightedIngredients.has(ingredient.name)) || activeIngredientHighlight === ingredient.name ? 600 : undefined}>
                  <Text component="span" fw={(highlightedIngredients.size > 0 && highlightedIngredients.has(ingredient.name)) || activeIngredientHighlight === ingredient.name ? 700 : 500}>
                    {formatIngredientQuantity(ingredient)}
                  </Text>
                  {' '}
                  {formatIngredientName(ingredient)}
                  {formatIngredientPreparation(ingredient) && (
                    <Text component="span" c="dimmed">
                      {' '}&mdash; {formatIngredientPreparation(ingredient)}
                    </Text>
                  )}
                </Text>
              </Box>
            ))}
          </Stack>
      </ResponsiveDrawer>

    </MobileOptimized>

      {/* Sticky sign-up CTA banner for unauthenticated users viewing shared recipes */}
      {!user && !authLoading && (
        <Box
          ref={ctaBannerRef}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'linear-gradient(135deg, var(--mantine-color-orange-6) 0%, var(--mantine-color-red-6) 100%)',
            borderTop: '1px solid var(--mantine-color-orange-4)',
            boxShadow: '0 -2px 16px rgba(0,0,0,0.1)',
          }}
          py="sm"
          px="md"
        >
          <Group justify="center" align="center" gap="md" wrap="nowrap" maw={600} mx="auto">
            <Text fw={700} size="sm" c="white" style={{ flex: 1 }}>
              Love this recipe? Get personalized meal plans for your family.
            </Text>
            <AuthButton size="sm" variant="filled" styles={{ root: { backgroundColor: 'var(--mantine-color-orange-1)', color: 'var(--mantine-color-orange-9)', flexShrink: 0 } }}>
              Join for free
            </AuthButton>
          </Group>
        </Box>
      )}
    </>
  );
};
